// Central content for UserGuidePage (Handbuch + FAQ)
// DE = primary, EN = full translation, other languages fall back to EN.

export const guideContentDE = {
  "guide.title": "Hilfe & FAQ",
  "guide.subtitle": "Benutzerhandbuch und Antworten auf häufig gestellte Fragen",
  "guide.searchPlaceholder": "Handbuch und FAQ durchsuchen...",
  "guide.tabGuide": "Benutzerhandbuch",
  "guide.tabFaq": "FAQ",
  "guide.noResults": "Keine Ergebnisse für",
  "guide.topic": "Thema",
  "guide.topics": "Themen",
  "guide.vote.helpful": "War dieser Artikel hilfreich?",
  "guide.vote.up": "Hilfreich",
  "guide.vote.down": "Nicht hilfreich",
  "guide.vote.thanksUp": "Danke für dein Feedback!",
  "guide.vote.thanksDown": "Danke — wir verbessern den Artikel.",
  "guide.vote.failed": "Feedback fehlgeschlagen",

  "guide.categories": [
    {
      "id": "gettingStarted",
      "title": "Erste Schritte",
      "sections": [
        {
          "title": "Registrierung & Anmeldung",
          "content": "Um Surplify nutzen zu können, registrieren Sie sich zunächst mit Ihrer geschäftlichen E-Mail-Adresse. Nach der Registrierung erhalten Sie Zugang zu Ihrem persönlichen Bereich.\n\n**So registrieren Sie sich:**\n1. Klicken Sie oben rechts auf \"Registrieren\"\n2. Geben Sie Ihren Namen, E-Mail und Passwort ein\n3. Wählen Sie Ihre Rolle (Händler, Manager etc.)\n4. Nach der Registrierung werden Sie automatisch eingeloggt\n\n**Anmeldung:** Verwenden Sie Ihre registrierte E-Mail und Ihr Passwort auf der Login-Seite."
        },
        {
          "title": "Übersicht der Navigation",
          "content": "Das Hauptmenü finden Sie oben rechts unter Ihrem Benutzernamen. Es ist in übersichtliche Gruppen gegliedert:\n\n- **Marktplatz** — Ihre Artikel, Merkliste, Gesuche, Standardmaterialien\n- **Handel & Kommunikation** — Anfragen, Transaktionen, Matching, Nachrichten\n- **Verwaltung** — Dashboard, Analytik, Import (nur für Manager/Admins)\n- **Portale** — Verkäufer-Portal, Gruppen-Admin Portal, Externes Portal\n- **Entwickler** — API, Webhooks, SDKs, CLI\n- **Einstellungen** — Benachrichtigungen, Hilfe, Sprache"
        },
        {
          "title": "Sprache ändern",
          "content": "Surplify ist mehrsprachig verfügbar (Deutsch, Englisch, Spanisch, Französisch, Italienisch, Portugiesisch, Rumänisch).\n\n**Sprache umstellen:**\n1. Klicken Sie in der Kopfzeile auf den Sprach-Umschalter (Globus-Symbol)\n2. Wählen Sie die gewünschte Sprache\n3. Die gesamte Oberfläche wird sofort übersetzt\n\nDie Auswahl wird in Ihrem Browser gespeichert und bei Ihrem nächsten Besuch automatisch geladen."
        }
      ]
    },
    {
      "id": "marketplace",
      "title": "Marktplatz",
      "sections": [
        {
          "title": "Material einstellen",
          "content": "Um ein Material auf dem Marktplatz einzustellen:\n\n1. Klicken Sie auf **\"Einstellen\"** in der oberen Navigationsleiste\n2. Füllen Sie das Formular aus: Name, Kategorie, Beschreibung, Menge, Preis\n3. Fügen Sie optionale Bilder und Spezifikationen hinzu\n4. Klicken Sie auf \"Veröffentlichen\"\n\n**Tipps:** Detaillierte Beschreibungen und Bilder erhöhen die Sichtbarkeit Ihrer Materialien."
        },
        {
          "title": "Materialien suchen & finden",
          "content": "Nutzen Sie die zentrale Suchleiste, um Materialien zu finden. Sie können nach:\n\n- **Materialnamen** oder **Kategorien** suchen\n- **Filter** verwenden: Materialart, Preis, Menge, Standort\n- Ergebnisse nach Relevanz, Preis oder Datum sortieren\n\nDie Ergebnisseite unterstützt zudem Sortierung (Datum, Preis, Menge) und Pagination für große Treffermengen."
        },
        {
          "title": "Merkliste (Watchlist)",
          "content": "Mit der **Merkliste** behalten Sie interessante Materialien im Blick, ohne sofort anzufragen.\n\n**So funktioniert's:**\n1. Klicken Sie auf das **Herz-Symbol** auf einer Material-Kachel oder Detailseite\n2. Das Material wird zu Ihrer persönlichen Merkliste hinzugefügt\n3. Öffnen Sie \"Merkliste\" im Menü, um alle gespeicherten Materialien zu sehen\n\n**Benachrichtigungen:** Sie erhalten eine Nachricht, wenn der Preis fällt oder sich der Status eines Materials auf Ihrer Merkliste ändert."
        },
        {
          "title": "Gesuche erstellen",
          "content": "Unter **\"Gesuche\"** können Sie aktive Suchanfragen einstellen:\n\n1. Navigieren Sie zu Gesuche\n2. Erstellen Sie ein neues Gesuch mit Material-Art, gewünschter Menge und Budget\n3. Andere Verkäufer können Ihre Gesuche sehen und darauf reagieren\n\nAktive Gesuche werden auch vom Matching-System berücksichtigt."
        },
        {
          "title": "Standardmaterialien",
          "content": "Die Standardmaterialien-Liste enthält vordefinierte Materialtypen für Ihr Unternehmen. Diese dienen als Vorlage und können bei der Artikelerstellung als Referenz verwendet werden. Manager können die Liste zentral pflegen, damit Inserate konsistent bleiben."
        }
      ]
    },
    {
      "id": "trading",
      "title": "Handel & Transaktionen",
      "sections": [
        {
          "title": "Anfragen senden & verwalten",
          "content": "Wenn Sie Interesse an einem Material haben:\n\n1. Öffnen Sie die Materialdetailseite\n2. Klicken Sie auf **\"Anfrage senden\"**\n3. Geben Sie Ihre gewünschte Menge und einen optionalen Kommentar ein\n4. Die Anfrage wird an den Verkäufer gesendet\n\n**Eingehende Anfragen** finden Sie unter \"Anfragen\" in Ihrem Menü mit Status-Filter (Offen, Angenommen, Abgelehnt)."
        },
        {
          "title": "Transaktionen",
          "content": "Unter **\"Transaktionen\"** sehen Sie alle laufenden und abgeschlossenen Geschäfte:\n\n- **Status:** Offen, In Verhandlung, Abgeschlossen, Storniert\n- **Details:** Menge, Preis, Käufer/Verkäufer-Info, Anhänge\n- Jede Transaktion kann kommentiert werden und generiert automatisch ein Nachrichten-Thread"
        },
        {
          "title": "Intelligentes Matching",
          "content": "Das **Matching-System** vergleicht Ihre Angebote automatisch mit passenden Gesuchen und umgekehrt:\n\n- Matches werden in Echtzeit berechnet (Material-Typ, Menge, Region, Preisspanne)\n- Sie erhalten E-Mail-Benachrichtigungen bei neuen Matches\n- Klicken Sie auf einen Match, um direkt eine Anfrage oder Nachricht zu starten\n\nDas Matching-Dashboard (Manager/Admin) zeigt Trefferquoten und Top-Paarungen."
        }
      ]
    },
    {
      "id": "messaging",
      "title": "Nachrichten & Angebote",
      "sections": [
        {
          "title": "Nachrichten-Threads",
          "content": "Das integrierte **Nachrichtensystem** ermöglicht direkte 1:1-Kommunikation zwischen Käufer und Verkäufer:\n\n- Jede Transaktion erzeugt automatisch einen Thread\n- Ungelesene Nachrichten werden im Menü mit einer Zahl angezeigt\n- Sie können Dateien (z. B. Spezifikationen, Fotos) an Nachrichten anhängen"
        },
        {
          "title": "Angebote senden",
          "content": "In jedem Nachrichten-Thread können Sie ein **verbindliches Angebot** senden:\n\n1. Öffnen Sie den Thread\n2. Klicken Sie auf **\"Angebot senden\"**\n3. Geben Sie Preis, Menge und optionale Konditionen ein (z. B. Abholfrist, Zahlungsbedingungen)\n4. Das Angebot erscheint als hervorgehobene Nachricht mit den Aktions-Buttons \"Annehmen\" / \"Ablehnen\"\n\nAngebote haben einen klaren Status: **Offen**, **Angenommen**, **Abgelehnt** oder **Zurückgezogen**."
        },
        {
          "title": "Angebot annehmen oder ablehnen",
          "content": "Als Empfänger eines Angebots:\n\n- **Annehmen:** Klicken Sie auf \"Annehmen\". Die Transaktion wird automatisch auf den vereinbarten Preis aktualisiert, beide Parteien erhalten eine **automatische E-Mail-Bestätigung** mit allen Details (Menge, Preis, Gegenpartei, Kontaktdaten).\n- **Ablehnen:** Klicken Sie auf \"Ablehnen\". Das Angebot wird als abgelehnt markiert. Sie können ein Gegenangebot senden.\n\nAlle Aktionen werden im Thread-Verlauf protokolliert."
        },
        {
          "title": "Gegenangebote & Verhandlung",
          "content": "Sie können jederzeit ein **Gegenangebot** senden, indem Sie einfach ein neues Angebot mit anderen Konditionen abschicken. Das bisherige Angebot bleibt sichtbar, aber das aktuellste Angebot ist bindend, sobald es angenommen wird."
        }
      ]
    },
    {
      "id": "subscriptions",
      "title": "Abonnements & Zahlungen",
      "sections": [
        {
          "title": "Abo-Modell & Tiers",
          "content": "Surplify bietet verschiedene Abonnement-Stufen:\n\n- **Basic** — Grundfunktionen für den Einstieg\n- **Professional** — Erweiterte Features, mehr Inserate\n- **Enterprise** — Volles Feature-Set inkl. Gruppen-Funktionalität und API-Zugriff\n\nUpgrades und Downgrades sind jederzeit über das **Verkäufer-Portal** möglich. Pro-rata Abrechnung erfolgt automatisch."
        },
        {
          "title": "Zahlung via Stripe",
          "content": "Alle Zahlungen laufen sicher über **Stripe**:\n\n1. Wählen Sie Ihr gewünschtes Abo-Tier\n2. Sie werden zur Stripe-Checkout-Seite weitergeleitet\n3. Nach erfolgreicher Zahlung wird Ihr Abo sofort aktiviert\n\nUnterstützte Methoden: Kreditkarte (Visa, Mastercard, Amex) und SEPA-Lastschrift. **Rechnungen** finden Sie im Verkäufer-Portal unter \"Rechnungen\"."
        },
        {
          "title": "SEPA-Lastschriftmandat",
          "content": "Für SEPA-Zahlungen können Sie Ihre **IBAN und BIC** hinterlegen:\n\n1. Gehen Sie zu Ihrem Portal (Verkäufer oder Gruppen-Admin)\n2. Unter \"Einstellungen\" finden Sie die Zahlungsdaten\n3. Tragen Sie IBAN und BIC ein\n4. Laden Sie Ihr SEPA-Lastschriftmandat als PDF herunter\n\nDas Mandat wird automatisch mit Ihren aktuellen Firmendaten generiert und ist SEPA-konform."
        },
        {
          "title": "Rechnungen & USt-ID",
          "content": "- **Rechnungen** werden automatisch als PDF erstellt und können jederzeit heruntergeladen werden\n- **USt-ID Validierung:** Ihre Umsatzsteuer-ID wird über das EU VIES System automatisch geprüft\n- **Rechnungsadresse:** Kann in den Einstellungen Ihres Portals angepasst werden\n- **Reverse-Charge** wird automatisch angewendet, wenn eine gültige EU-USt-ID außerhalb Deutschlands hinterlegt ist"
        }
      ]
    },
    {
      "id": "groups",
      "title": "Gruppen & Einladungen",
      "sections": [
        {
          "title": "Gruppen-Konzept",
          "content": "**Company Groups** ermöglichen es, mehrere Standorte unter einem Dach zu verwalten:\n\n- Ein **Gruppen-Admin** verwaltet die gesamte Gruppe\n- Einzelne **Standorte (Sites)** sind Mitglieder der Gruppe\n- Gemeinsame Abrechnung und zentrale Verwaltung\n- Optional mit Kostenstellen pro Standort"
        },
        {
          "title": "Einladungen",
          "content": "Als Gruppen-Admin können Sie neue Mitglieder einladen:\n\n1. Gehen Sie zum **Gruppen-Admin Portal** > \"Einladungen\"\n2. Wählen Sie \"Per E-Mail einladen\" oder \"Per Standort einladen\"\n3. Der Eingeladene erhält eine E-Mail mit einem Link\n4. Nach Annahme wird der Standort zur Gruppe hinzugefügt\n\n**Status:** Ausstehend, Angenommen, Abgelehnt, Abgelaufen, Widerrufen"
        },
        {
          "title": "Gruppen-Admin Portal",
          "content": "Das Gruppen-Admin Portal bietet:\n\n- **Übersicht:** KPI-Karten, Gruppenstatus, Tier-Details\n- **Mitglieder:** Liste aller Standorte mit Kostenstellen-Bearbeitung\n- **Einladungen:** Neue Einladungen senden und verwalten\n- **Rechnungen:** Gruppen-Rechnungen als PDF\n- **Einstellungen:** Rechnungsadresse, USt-ID, SEPA-Daten"
        }
      ]
    },
    {
      "id": "sellerPortal",
      "title": "Verkäufer-Portal",
      "sections": [
        {
          "title": "Übersicht",
          "content": "Im **Verkäufer-Portal** verwalten Sie Ihr Abonnement und alle finanziellen Details:\n\n- **Abonnement:** Status, Tier-Details, Upgrade/Downgrade, Kündigung\n- **Rechnungen:** Alle Rechnungen mit PDF-Download\n- **Provisionen:** Übersicht über Provisionseinnahmen und Transaktionshistorie\n- **Einladungen:** Ausstehende Gruppeneinladungen annehmen oder ablehnen\n- **Einstellungen:** Rechnungsadresse, USt-ID, PO-Nummer, SEPA-Daten"
        }
      ]
    },
    {
      "id": "externalPortal",
      "title": "Externes Lieferanten-Portal",
      "sections": [
        {
          "title": "Was ist das externe Portal?",
          "content": "Das **externe Portal** ist ein dedizierter Bereich für externe Lieferanten oder Verarbeiter, die auf Anfragen reagieren möchten, ohne ein vollwertiges Surplify-Konto anzulegen.\n\n- Eingeschränkter Funktionsumfang: Anfragen einsehen, Angebote abgeben, Nachrichten austauschen\n- Keine Sichtbarkeit auf interne Materialien oder Gruppen\n- Ideal für gelegentliche Partner"
        },
        {
          "title": "Externe Registrierung",
          "content": "Externe Nutzer können sich selbst über eine **öffentliche Einladungsseite** registrieren oder auf einen Einladungslink eines Managers klicken. Nach der Registrierung ist das Konto sofort nutzbar, aber auf externe Ansichten beschränkt."
        },
        {
          "title": "Szenarien & externer Zugriff",
          "content": "Admins können über **Szenarien** steuern, ob und wann externe Firmen auf Anfragen zugreifen dürfen. Die Einstellung \"External Enabled\" im Scenario-Modus aktiviert oder blockiert den externen Zugang global."
        }
      ]
    },
    {
      "id": "commissions",
      "title": "Provisionen",
      "sections": [
        {
          "title": "Wie funktionieren Provisionen?",
          "content": "Für jede erfolgreich abgeschlossene Transaktion erhebt Surplify eine **prozentuale Provision** auf den Verkaufspreis. Die Höhe hängt vom Abo-Tier ab:\n\n- Basic: Standard-Provisionssatz\n- Professional: Reduzierter Satz\n- Enterprise: Individuell verhandelbar"
        },
        {
          "title": "Provisions-Dashboard",
          "content": "Unter **\"Provisionen\"** sehen Sie:\n\n- Aktuelle Provisionsmodelle (aktiv/historisch)\n- Transaktionshistorie mit berechneter Provision pro Deal\n- Monats-Summen und kumulierte Jahreszahlen\n- Fälligkeits-Status und Abrechnungen\n\nAdmins können Provisionsmodelle im Admin-Dashboard erstellen, aktivieren und versionieren."
        }
      ]
    },
    {
      "id": "analytics",
      "title": "Analytik",
      "sections": [
        {
          "title": "Analytik-Dashboard",
          "content": "Das **Analytik-Dashboard** liefert Manager- und Admin-Einblicke:\n\n- Top-Materialien (Views, Anfragen, Conversions)\n- Umsatz- und Transaktionsverlauf\n- Matching-Trefferquoten\n- Nutzeraktivität pro Standort/Gruppe\n\nAlle Diagramme sind nach Zeitraum filterbar (7/30/90 Tage, individuell)."
        },
        {
          "title": "Export & Reporting",
          "content": "Die Analytik-Ergebnisse lassen sich als **CSV oder Excel** exportieren. Für Buchhaltungszwecke steht zusätzlich der **DATEV-Export** im Admin-Dashboard zur Verfügung."
        }
      ]
    },
    {
      "id": "importing",
      "title": "Massen-Import (CSV/Excel)",
      "sections": [
        {
          "title": "Materialien per Upload einstellen",
          "content": "Manager und Admins können mehrere Materialien gleichzeitig importieren:\n\n1. Navigieren Sie zu **\"Import\"**\n2. Laden Sie die **Excel-Vorlage** herunter (zeigt das erwartete Spalten-Format)\n3. Füllen Sie die Vorlage mit Ihren Daten aus\n4. Laden Sie die Datei hoch\n5. Prüfen Sie die Validierungsergebnisse (Fehler werden pro Zeile markiert)\n6. Wählen Sie einen Import-Modus:\n   - **Alles überschreiben:** Ersetzt bestehende Artikel\n   - **Nur anreichern:** Fügt fehlende Felder hinzu, ohne bestehende zu überschreiben\n   - **Felder auswählen:** Feingranulare Kontrolle über aktualisierte Spalten\n7. Führen Sie den Import aus"
        }
      ]
    },
    {
      "id": "scenarios",
      "title": "Szenarien (Admin)",
      "sections": [
        {
          "title": "Szenario-Modi",
          "content": "Admins steuern das globale Plattform-Verhalten über **Szenarien**:\n\n- **Ausgewogen (Standard):** Normale Regeln für interne und externe Sichtbarkeit\n- **Konservativ:** Längere interne Sichtbarkeit, strengere Freigabe\n- **Aggressive Cash:** Schnelle Externalisierung, maximale Reichweite\n- **Krisen-Modus:** Externer Zugriff blockiert, nur interne Deals\n\nParameter wie \"Internes Fenster\" (Tage interne Sichtbarkeit) und \"Eskalations-Tage\" (Auto-Benachrichtigung bei alten Anfragen) lassen sich einstellen."
        }
      ]
    },
    {
      "id": "bugReports",
      "title": "Fehler melden",
      "sections": [
        {
          "title": "Bug-Report einreichen",
          "content": "Wenn Ihnen ein Fehler auffällt:\n\n1. Klicken Sie auf das **Käfer-Symbol** in der Kopfzeile oder rufen Sie **\"Meine Fehlerberichte\"** auf\n2. Beschreiben Sie den Fehler möglichst präzise (URL, Schritte, erwartetes Verhalten)\n3. Hängen Sie einen Screenshot an (optional aber empfohlen)\n4. Absenden\n\nSie sehen im Status-Tracker, ob das Team den Bericht bestätigt (\"Offen\"), bearbeitet (\"In Bearbeitung\") oder behoben (\"Erledigt\") hat."
        }
      ]
    },
    {
      "id": "notifications",
      "title": "Benachrichtigungen",
      "sections": [
        {
          "title": "E-Mail-Benachrichtigungen konfigurieren",
          "content": "Surplify sendet automatische E-Mails bei wichtigen Ereignissen:\n\n- Neue Anfragen und Angebote\n- **Angebotsannahme** (Käufer + Verkäufer erhalten je eine Bestätigung)\n- Matching-Treffer\n- Zahlungseingänge und -erinnerungen\n- Einladungen und Gruppenänderungen\n- Eskalationen (alte, unbearbeitete Anfragen)\n\nUnter **\"E-Mail-Benachrichtigungen\"** in den Einstellungen können Sie pro Ereignis-Typ festlegen, welche E-Mails Sie erhalten möchten."
        }
      ]
    },
    {
      "id": "developerApi",
      "title": "Entwickler: API, Webhooks, SDKs & CLI",
      "sections": [
        {
          "title": "Öffentliche REST-API",
          "content": "Surplify bietet eine vollständige **REST-API** (Versionen v1 & v2) für die Integration in Ihre ERP- oder Logistik-Systeme:\n\n- Basis-URL: `https://api.surplify.de/api/v1/`\n- Authentifizierung via **API-Key** (im Developer-Portal erstellen)\n- Rate-Limits pro Key\n- OpenAPI-Spezifikation verfügbar\n\nDie interaktive API-Referenz finden Sie unter **\"Entwickler\" > API Dokumentation**."
        },
        {
          "title": "Outbound Webhooks (HMAC-signiert)",
          "content": "Empfangen Sie Echtzeit-Events in Ihren Systemen:\n\n1. Erstellen Sie einen Webhook-Endpoint im Developer-Portal\n2. Abonnieren Sie Events (z. B. `material.created`, `transaction.completed`, `offer.accepted`)\n3. Jede Payload ist **HMAC-SHA256 signiert** (Header: `X-Surplify-Signature`)\n4. Automatische Retries bei Zustellfehlern mit exponentiellem Backoff\n\nValidieren Sie Signaturen mit dem in den Docs beschriebenen Algorithmus, um Manipulationen auszuschließen."
        },
        {
          "title": "Client-SDKs (Python, Node.js, PHP)",
          "content": "Dynamisch generierte SDKs stehen zum Download bereit:\n\n- **Python SDK** (pip-Paket)\n- **Node.js SDK** (npm-Paket)\n- **PHP SDK** (Composer)\n\nDie SDKs sind vorkonfiguriert mit Auth-Handling, Rate-Limit-Retries und typisierten Modellen. Laden Sie sie im Developer-Portal herunter."
        },
        {
          "title": "CLI: npx create-surplify-app",
          "content": "Mit dem offiziellen **Scaffolding-Tool** starten Sie in Sekunden ein neues Integrationsprojekt:\n\n```bash\nnpx create-surplify-app my-integration\ncd my-integration\nnpm install\nnpm run dev\n```\n\nDas generierte Projekt enthält:\n- Vorkonfigurierte Webhook-Listener mit HMAC-Validierung\n- Beispiel-API-Calls (Materialien listen, Anfragen senden)\n- `.env`-Template für API-Key, Webhook-Secret und Base-URL\n- Tests & Hot-Reload\n\nPerfekt, um neue Integrationen in Minuten statt Tagen zu bauen."
        }
      ]
    },
    {
      "id": "adminOverview",
      "title": "Admin-Portal Überblick",
      "sections": [
        {
          "title": "Was kann ein Plattform-Admin?",
          "content": "Plattform-Admins haben Zugriff auf alle Verwaltungsebenen:\n\n- **Benutzer & Rollen:** Anlegen, sperren, Rollen ändern\n- **Abonnements:** Manuelle Anpassungen, Kulanzgutschriften\n- **Provisionen:** Modelle erstellen und versionieren\n- **SEPA-Mandate:** Einsicht und Freigabe\n- **Rechnungen:** Alle Rechnungen plattformweit\n- **DATEV-Export:** Buchhaltungs-Daten generieren\n- **Szenarien:** Globales Plattform-Verhalten steuern\n- **Audit-Log:** Alle sicherheitsrelevanten Aktionen nachvollziehbar"
        }
      ]
    }
  ],

  "guide.faq": [
    {
      "category": "Allgemein",
      "questions": [
        { "q": "Was ist Surplify?", "a": "Surplify ist ein B2B-Marktplatz für Verpackungsmaterialien. Unternehmen können überschüssige Materialien anbieten oder gezielt nach benötigten Materialien suchen." },
        { "q": "Wer kann Surplify nutzen?", "a": "Surplify richtet sich an Unternehmen der Verpackungsbranche — Händler, Hersteller und Verarbeiter. Sowohl kleine als auch große Unternehmen profitieren von der Plattform." },
        { "q": "Ist die Nutzung kostenpflichtig?", "a": "Die Registrierung ist kostenlos. Für erweiterte Funktionen wie mehr Inserate, Matching oder API-Zugriff stehen kostenpflichtige Tiers zur Verfügung." },
        { "q": "In welchen Sprachen ist Surplify verfügbar?", "a": "Sieben Sprachen: Deutsch, Englisch, Spanisch, Französisch, Italienisch, Portugiesisch und Rumänisch. Die Umschaltung erfolgt über den Globus in der Kopfzeile." }
      ]
    },
    {
      "category": "Konto & Registrierung",
      "questions": [
        { "q": "Wie erstelle ich ein Konto?", "a": "Klicken Sie auf \"Registrieren\", füllen Sie das Formular aus und bestätigen Sie Ihre E-Mail-Adresse. Sie können sofort loslegen." },
        { "q": "Kann ich meine E-Mail-Adresse ändern?", "a": "Kontaktieren Sie den Support, um Ihre E-Mail-Adresse zu ändern. Aus Sicherheitsgründen ist dies nicht selbst möglich." },
        { "q": "Was passiert, wenn ich mein Passwort vergesse?", "a": "Nutzen Sie die \"Passwort vergessen\"-Funktion auf der Login-Seite. Sie erhalten eine E-Mail mit einem Link zum Zurücksetzen." },
        { "q": "Welche Rollen gibt es?", "a": "Händler, Manager (Admin eines Standorts), Gruppen-Admin (verwaltet mehrere Standorte), Plattform-Admin und externe Lieferanten. Die Rolle bestimmt, welche Menüpunkte sichtbar sind." }
      ]
    },
    {
      "category": "Handel & Materialien",
      "questions": [
        { "q": "Wie stelle ich ein Material ein?", "a": "Klicken Sie auf \"Einstellen\", füllen Sie das Formular mit allen relevanten Details aus und veröffentlichen Sie Ihr Angebot. Es erscheint sofort auf dem Marktplatz." },
        { "q": "Wie funktioniert das Matching?", "a": "Das Matching-System vergleicht automatisch Angebote und Gesuche basierend auf Materialart, Menge und Standort. Bei Übereinstimmungen werden beide Parteien benachrichtigt." },
        { "q": "Kann ich Materialien in großen Mengen importieren?", "a": "Ja, Manager und Admins können über die Import-Funktion mehrere Materialien gleichzeitig per Excel-Upload einstellen (mit Validierung und Dry-Run)." },
        { "q": "Was sind Gesuche?", "a": "Gesuche sind aktive Suchanfragen. Wenn Sie ein bestimmtes Material benötigen, erstellen Sie ein Gesuch. Verkäufer können darauf reagieren und Ihnen Angebote machen." },
        { "q": "Wofür ist die Merkliste?", "a": "Die Merkliste speichert Materialien, die Sie im Auge behalten möchten, ohne sofort eine Anfrage zu senden. Bei Preis- oder Status-Änderungen erhalten Sie optional eine Benachrichtigung." }
      ]
    },
    {
      "category": "Nachrichten & Angebote",
      "questions": [
        { "q": "Wie sende ich ein verbindliches Angebot?", "a": "Öffnen Sie den Nachrichten-Thread zur Transaktion, klicken Sie auf \"Angebot senden\" und geben Sie Preis, Menge und Konditionen ein. Das Angebot ist in der Chat-Historie hervorgehoben." },
        { "q": "Was passiert, wenn ein Angebot angenommen wird?", "a": "Die Transaktion wird automatisch auf den vereinbarten Preis aktualisiert, und beide Parteien erhalten eine E-Mail-Bestätigung mit allen Details (Menge, Preis, Kontaktdaten der Gegenpartei)." },
        { "q": "Kann ich ein Angebot zurückziehen?", "a": "Ja, solange der Empfänger noch nicht angenommen hat, können Sie im Thread \"Zurückziehen\" wählen. Nach Annahme ist das Angebot bindend." },
        { "q": "Wie funktioniert ein Gegenangebot?", "a": "Senden Sie einfach ein neues Angebot mit anderen Konditionen. Das vorherige bleibt in der Historie sichtbar, aber nur das zuletzt akzeptierte Angebot ist bindend." }
      ]
    },
    {
      "category": "Zahlungen & Rechnungen",
      "questions": [
        { "q": "Welche Zahlungsmethoden werden unterstützt?", "a": "Zahlungen laufen über Stripe. Kreditkarte (Visa, Mastercard, Amex) und SEPA-Lastschrift werden unterstützt." },
        { "q": "Wo finde ich meine Rechnungen?", "a": "Im Verkäufer-Portal unter \"Rechnungen\" oder im Gruppen-Admin Portal. Alle Rechnungen können als PDF heruntergeladen werden." },
        { "q": "Was ist ein SEPA-Lastschriftmandat?", "a": "Das SEPA-Lastschriftmandat autorisiert Surplify, Zahlungen von Ihrem Bankkonto einzuziehen. Sie können es unter Einstellungen generieren, nachdem Sie Ihre IBAN/BIC hinterlegt haben." },
        { "q": "Wie validiere ich meine USt-ID?", "a": "Surplify prüft Ihre Umsatzsteuer-Identifikationsnummer automatisch über das EU VIES System. Geben Sie diese in den Einstellungen Ihres Portals ein." },
        { "q": "Wird Reverse-Charge angewendet?", "a": "Ja, automatisch bei gültiger EU-USt-ID außerhalb Deutschlands. Die Rechnung enthält dann den entsprechenden Hinweis." }
      ]
    },
    {
      "category": "Gruppen & Standorte",
      "questions": [
        { "q": "Was ist eine Company Group?", "a": "Eine Company Group fasst mehrere Standorte (Sites) eines Unternehmens zusammen. Die Gruppe wird zentral verwaltet und abgerechnet." },
        { "q": "Wie lade ich Standorte in meine Gruppe ein?", "a": "Im Gruppen-Admin Portal unter \"Einladungen\". Sie können per E-Mail oder durch Standort-Suche einladen. Einladungen laufen nach einer bestimmten Zeit ab." },
        { "q": "Kann ein Standort in mehreren Gruppen sein?", "a": "Nein, ein Standort kann zu jedem Zeitpunkt nur einer Gruppe angehören." }
      ]
    },
    {
      "category": "Externes Portal",
      "questions": [
        { "q": "Wer ist das externe Portal gedacht?", "a": "Für gelegentliche externe Partner, die auf Anfragen reagieren oder Angebote abgeben möchten, ohne ein vollwertiges Konto zu führen." },
        { "q": "Wie registriere ich mich als externer Lieferant?", "a": "Über einen Einladungslink eines Managers oder die öffentliche Registrierungsseite für externe Anbieter." },
        { "q": "Kann ich zwischen externem und normalem Konto wechseln?", "a": "Ein Upgrade von extern zu vollwertigem Konto ist möglich — kontaktieren Sie dazu den Support oder den einladenden Manager." }
      ]
    },
    {
      "category": "Administration",
      "questions": [
        { "q": "Was kann ein Plattform-Admin tun?", "a": "Benutzerverwaltung, Abonnement-Management, Provisionen, SEPA-Mandate, Rechnungen, DATEV-Export, Szenarien, Audit-Log und alle analytischen Dashboards." },
        { "q": "Was ist der DATEV-Export?", "a": "Der DATEV-Export generiert Buchungsdaten im DATEV-Format für die Steuerberatung. Dies ist unter dem Admin-Dashboard (Exporte-Tab) verfügbar." },
        { "q": "Wie verwalte ich Provisionen?", "a": "Im Provisions-Dashboard können Admins Provisions-Modelle konfigurieren, Transaktionen einsehen und Abrechnungen erstellen." },
        { "q": "Was ist das Audit-Log?", "a": "Ein unveränderbares Protokoll aller sicherheitsrelevanten Aktionen (Logins, Rollenänderungen, Rechnungsanpassungen) — verfügbar im Admin-Dashboard." }
      ]
    },
    {
      "category": "Entwickler & API",
      "questions": [
        { "q": "Wie erhalte ich einen API-Key?", "a": "Im Developer-Portal unter \"API-Keys\" können Sie einen neuen Key erstellen. Bewahren Sie ihn sicher auf — er wird nur einmal angezeigt." },
        { "q": "Gibt es Rate-Limits?", "a": "Ja, pro API-Key. Die genauen Limits sind im Developer-Portal einsehbar und hängen vom Abo-Tier ab. Bei Überschreitung erhalten Sie HTTP 429." },
        { "q": "Wie validiere ich Webhook-Signaturen?", "a": "Jede Payload wird mit HMAC-SHA256 signiert (Header X-Surplify-Signature). Verwenden Sie Ihr Webhook-Secret, um die Signatur zu validieren — Code-Beispiele finden Sie in der API-Dokumentation." },
        { "q": "Was ist create-surplify-app?", "a": "Ein offizielles Scaffolding-Tool (npx create-surplify-app my-integration), das in Sekunden ein vorkonfiguriertes Integrations-Projekt mit Webhook-Listener, API-Client und Tests erstellt." },
        { "q": "Welche SDKs gibt es?", "a": "Python, Node.js und PHP. Alle sind dynamisch generiert und enthalten Auth-Handling, Retries und typisierte Modelle." }
      ]
    },
    {
      "category": "Support & Sprachen",
      "questions": [
        { "q": "Wie melde ich einen Fehler?", "a": "Klicken Sie auf das Käfer-Symbol in der Kopfzeile oder öffnen Sie \"Meine Fehlerberichte\". Beschreiben Sie das Problem und fügen Sie optional einen Screenshot bei." },
        { "q": "Wie ändere ich die Sprache?", "a": "Oben rechts in der Kopfzeile finden Sie den Globus — klicken Sie darauf und wählen Sie die gewünschte Sprache. Die Auswahl wird gespeichert." },
        { "q": "Wie kontaktiere ich den Support?", "a": "Für dringende Anliegen nutzen Sie die Kontaktdaten im Impressum oder schreiben Sie direkt aus dem Portal (\"Hilfe > Support kontaktieren\")." }
      ]
    }
  ]
};

export const guideContentEN = {
  "guide.title": "Help & FAQ",
  "guide.subtitle": "User manual and answers to frequently asked questions",
  "guide.searchPlaceholder": "Search manual and FAQ...",
  "guide.tabGuide": "User Manual",
  "guide.tabFaq": "FAQ",
  "guide.noResults": "No results for",
  "guide.topic": "topic",
  "guide.topics": "topics",
  "guide.vote.helpful": "Was this article helpful?",
  "guide.vote.up": "Helpful",
  "guide.vote.down": "Not helpful",
  "guide.vote.thanksUp": "Thanks for your feedback!",
  "guide.vote.thanksDown": "Thanks — we'll improve the article.",
  "guide.vote.failed": "Feedback failed",

  "guide.categories": [
    {
      "id": "gettingStarted",
      "title": "Getting Started",
      "sections": [
        {
          "title": "Registration & Login",
          "content": "To use Surplify, register with your business email. After signing up, you get access to your personal dashboard.\n\n**How to register:**\n1. Click \"Register\" at the top right\n2. Enter your name, email and password\n3. Choose your role (Trader, Manager, etc.)\n4. After registration you're logged in automatically\n\n**Login:** Use your registered email and password on the login page."
        },
        {
          "title": "Navigation overview",
          "content": "The main menu is at the top right under your username. It's grouped into:\n\n- **Marketplace** — your items, watchlist, wanted, standard materials\n- **Trading & Communication** — requests, transactions, matching, messages\n- **Administration** — dashboard, analytics, import (managers/admins only)\n- **Portals** — seller portal, group admin portal, external portal\n- **Developers** — API, webhooks, SDKs, CLI\n- **Settings** — notifications, help, language"
        },
        {
          "title": "Changing the language",
          "content": "Surplify is available in multiple languages (German, English, Spanish, French, Italian, Portuguese, Romanian).\n\n**To switch language:**\n1. Click the language switcher (globe icon) in the header\n2. Select the desired language\n3. The entire UI is translated instantly\n\nYour choice is saved in the browser and restored on your next visit."
        }
      ]
    },
    {
      "id": "marketplace",
      "title": "Marketplace",
      "sections": [
        {
          "title": "Listing a material",
          "content": "To list a material on the marketplace:\n\n1. Click **\"List\"** in the top navigation\n2. Fill the form: name, category, description, quantity, price\n3. Add optional images and specifications\n4. Click \"Publish\"\n\n**Tips:** Detailed descriptions and images increase visibility."
        },
        {
          "title": "Searching & finding materials",
          "content": "Use the central search bar to find materials. You can search by:\n\n- **Material name** or **category**\n- **Filters:** type, price, quantity, location\n- Sort results by relevance, price or date\n\nPagination and sort (date, price, quantity) are available for large result sets."
        },
        {
          "title": "Watchlist",
          "content": "The **Watchlist** lets you keep an eye on interesting materials without inquiring immediately.\n\n**How it works:**\n1. Click the **heart icon** on a material card or detail page\n2. The material is added to your personal watchlist\n3. Open \"Watchlist\" in the menu to see all saved items\n\n**Notifications:** You can opt in to receive alerts when the price drops or the status of a watched material changes."
        },
        {
          "title": "Creating wanted posts",
          "content": "Under **\"Wanted\"** you can post active buying requests:\n\n1. Navigate to Wanted\n2. Create a new wanted post with type, quantity and budget\n3. Sellers can see your wanted posts and respond\n\nActive wanted posts are also considered by the matching engine."
        },
        {
          "title": "Standard materials",
          "content": "The standard materials list contains predefined material types for your organization. They serve as a template when creating listings so that inventory stays consistent. Managers can maintain the list centrally."
        }
      ]
    },
    {
      "id": "trading",
      "title": "Trading & Transactions",
      "sections": [
        {
          "title": "Sending & managing requests",
          "content": "If you're interested in a material:\n\n1. Open the material detail page\n2. Click **\"Send request\"**\n3. Enter the desired quantity and an optional comment\n4. The request is sent to the seller\n\n**Incoming requests** are under \"Requests\" in your menu with status filters (Open, Accepted, Declined)."
        },
        {
          "title": "Transactions",
          "content": "Under **\"Transactions\"** you see all ongoing and closed deals:\n\n- **Status:** Open, Negotiating, Completed, Cancelled\n- **Details:** quantity, price, buyer/seller info, attachments\n- Each transaction can be commented and automatically spawns a message thread"
        },
        {
          "title": "Smart matching",
          "content": "The **matching engine** automatically compares your listings with wanted posts and vice-versa:\n\n- Matches are computed in real time (material type, quantity, region, price range)\n- You get email alerts on new matches\n- Click a match to start a request or message directly\n\nThe matching dashboard (manager/admin) shows hit-rate and top pairings."
        }
      ]
    },
    {
      "id": "messaging",
      "title": "Messages & Offers",
      "sections": [
        {
          "title": "Message threads",
          "content": "The integrated **messaging system** allows direct 1:1 communication between buyer and seller:\n\n- Each transaction spawns a thread automatically\n- Unread messages are shown as a badge in the menu\n- You can attach files (specs, photos) to messages"
        },
        {
          "title": "Sending offers",
          "content": "In any thread you can send a **binding offer**:\n\n1. Open the thread\n2. Click **\"Send offer\"**\n3. Enter price, quantity and optional conditions (pickup window, payment terms)\n4. The offer appears as a highlighted message with \"Accept\" / \"Decline\" action buttons\n\nOffers have a clear status: **Open**, **Accepted**, **Declined**, or **Withdrawn**."
        },
        {
          "title": "Accepting or declining an offer",
          "content": "As the recipient of an offer:\n\n- **Accept:** Click \"Accept\". The transaction is auto-updated to the agreed price and both parties receive an **automatic email confirmation** with all details (quantity, price, counterparty, contact).\n- **Decline:** Click \"Decline\". The offer is marked declined. You can send a counter-offer.\n\nAll actions are logged in the thread history."
        },
        {
          "title": "Counter-offers & negotiation",
          "content": "You can send a **counter-offer** at any time by simply submitting a new offer with different terms. The previous offer stays visible in the history, but only the most recent accepted offer is binding."
        }
      ]
    },
    {
      "id": "subscriptions",
      "title": "Subscriptions & Payments",
      "sections": [
        {
          "title": "Plans & tiers",
          "content": "Surplify offers several subscription tiers:\n\n- **Basic** — core features to get started\n- **Professional** — advanced features, more listings\n- **Enterprise** — full feature set incl. group functionality and API access\n\nUpgrades and downgrades are available anytime from the **Seller Portal** with automatic pro-rata billing."
        },
        {
          "title": "Payments via Stripe",
          "content": "All payments run securely through **Stripe**:\n\n1. Choose your desired tier\n2. You're redirected to Stripe Checkout\n3. On successful payment your subscription is activated instantly\n\nSupported methods: credit card (Visa, Mastercard, Amex) and SEPA direct debit. **Invoices** are available in the Seller Portal under \"Invoices\"."
        },
        {
          "title": "SEPA direct debit mandate",
          "content": "For SEPA payments you can store your **IBAN and BIC**:\n\n1. Go to your Portal (seller or group admin)\n2. Under \"Settings\" you'll find the payment details\n3. Enter IBAN and BIC\n4. Download your SEPA mandate as PDF\n\nThe mandate is auto-generated from your current company data and fully SEPA-compliant."
        },
        {
          "title": "Invoices & VAT ID",
          "content": "- **Invoices** are auto-generated as PDF and can be downloaded anytime\n- **VAT ID validation** runs automatically via the EU VIES system\n- **Billing address** can be edited in your portal settings\n- **Reverse charge** is automatically applied when a valid EU VAT ID outside Germany is stored"
        }
      ]
    },
    {
      "id": "groups",
      "title": "Groups & Invitations",
      "sections": [
        {
          "title": "Group concept",
          "content": "**Company Groups** let you manage multiple sites under one umbrella:\n\n- A **group admin** manages the whole group\n- Individual **sites** are members of the group\n- Shared billing and central administration\n- Optional cost centers per site"
        },
        {
          "title": "Invitations",
          "content": "As a group admin you can invite new members:\n\n1. Go to the **Group Admin Portal** > \"Invitations\"\n2. Choose \"Invite by email\" or \"Invite by site\"\n3. The invitee receives an email with a link\n4. On acceptance the site joins the group\n\n**Status:** Pending, Accepted, Declined, Expired, Revoked"
        },
        {
          "title": "Group Admin Portal",
          "content": "The Group Admin Portal offers:\n\n- **Overview:** KPI cards, group status, tier details\n- **Members:** list of all sites with cost-center editing\n- **Invitations:** send and manage invitations\n- **Invoices:** group invoices as PDF\n- **Settings:** billing address, VAT ID, SEPA details"
        }
      ]
    },
    {
      "id": "sellerPortal",
      "title": "Seller Portal",
      "sections": [
        {
          "title": "Overview",
          "content": "The **Seller Portal** is where you manage your subscription and all financial details:\n\n- **Subscription:** status, tier details, upgrade/downgrade, cancel\n- **Invoices:** all invoices with PDF download\n- **Commissions:** overview of commission revenue and transaction history\n- **Invitations:** accept or decline pending group invitations\n- **Settings:** billing address, VAT ID, PO number, SEPA data"
        }
      ]
    },
    {
      "id": "externalPortal",
      "title": "External Supplier Portal",
      "sections": [
        {
          "title": "What is the external portal?",
          "content": "The **external portal** is a dedicated area for external suppliers or processors who want to respond to requests without a full Surplify account.\n\n- Limited feature set: see requests, submit offers, exchange messages\n- No visibility into internal materials or groups\n- Ideal for occasional partners"
        },
        {
          "title": "External registration",
          "content": "External users can register themselves via a **public invitation page** or via an invitation link sent by a manager. After registration the account is immediately usable but limited to external views."
        },
        {
          "title": "Scenarios & external access",
          "content": "Admins can control whether and when external companies can access requests via **scenarios**. The \"External Enabled\" setting in the scenario configuration globally enables or blocks external access."
        }
      ]
    },
    {
      "id": "commissions",
      "title": "Commissions",
      "sections": [
        {
          "title": "How do commissions work?",
          "content": "For each successfully closed transaction Surplify charges a **percentage commission** on the sale price. The rate depends on the subscription tier:\n\n- Basic: standard rate\n- Professional: reduced rate\n- Enterprise: individually negotiable"
        },
        {
          "title": "Commission dashboard",
          "content": "Under **\"Commissions\"** you can see:\n\n- Current commission models (active/historical)\n- Transaction history with calculated commission per deal\n- Monthly totals and cumulative yearly figures\n- Due status and settlements\n\nAdmins can create, activate and version commission models in the Admin Dashboard."
        }
      ]
    },
    {
      "id": "analytics",
      "title": "Analytics",
      "sections": [
        {
          "title": "Analytics dashboard",
          "content": "The **analytics dashboard** gives managers and admins insights into:\n\n- Top materials (views, requests, conversions)\n- Revenue and transaction trends\n- Matching hit rates\n- User activity per site/group\n\nAll charts can be filtered by time range (7/30/90 days, custom)."
        },
        {
          "title": "Export & reporting",
          "content": "Analytics results can be exported as **CSV or Excel**. For accounting a **DATEV export** is also available in the Admin Dashboard."
        }
      ]
    },
    {
      "id": "importing",
      "title": "Bulk import (CSV/Excel)",
      "sections": [
        {
          "title": "List materials via upload",
          "content": "Managers and admins can import multiple materials at once:\n\n1. Navigate to **\"Import\"**\n2. Download the **Excel template** (shows the expected column format)\n3. Fill the template with your data\n4. Upload the file\n5. Review validation results (errors are highlighted per row)\n6. Choose an import mode:\n   - **Overwrite all:** replaces existing items\n   - **Enrich only:** adds missing fields without overwriting existing ones\n   - **Select fields:** fine-grained control over updated columns\n7. Execute the import"
        }
      ]
    },
    {
      "id": "scenarios",
      "title": "Scenarios (Admin)",
      "sections": [
        {
          "title": "Scenario modes",
          "content": "Admins steer global platform behavior through **scenarios**:\n\n- **Balanced (default):** standard rules for internal and external visibility\n- **Conservative:** longer internal visibility, stricter approval\n- **Aggressive Cash:** fast externalization, maximum reach\n- **Crisis mode:** external access blocked, internal deals only\n\nParameters like \"internal window\" (days of internal-only visibility) and \"escalation days\" (auto-notification for aging requests) are configurable."
        }
      ]
    },
    {
      "id": "bugReports",
      "title": "Reporting issues",
      "sections": [
        {
          "title": "Submitting a bug report",
          "content": "If you spot a bug:\n\n1. Click the **bug icon** in the header or open **\"My Bug Reports\"**\n2. Describe the issue as precisely as possible (URL, steps, expected behavior)\n3. Attach a screenshot (optional but recommended)\n4. Submit\n\nThe status tracker shows whether the team has acknowledged (\"Open\"), is working on (\"In progress\") or has fixed (\"Done\") the report."
        }
      ]
    },
    {
      "id": "notifications",
      "title": "Notifications",
      "sections": [
        {
          "title": "Configuring email notifications",
          "content": "Surplify sends automatic emails for key events:\n\n- New requests and offers\n- **Offer acceptance** (buyer + seller each get a confirmation)\n- Matching hits\n- Payments and reminders\n- Invitations and group changes\n- Escalations (old, unhandled requests)\n\nUnder **\"Email notifications\"** in settings you can toggle each event type individually."
        }
      ]
    },
    {
      "id": "developerApi",
      "title": "Developer: API, Webhooks, SDKs & CLI",
      "sections": [
        {
          "title": "Public REST API",
          "content": "Surplify offers a full **REST API** (v1 & v2) for integration into your ERP or logistics systems:\n\n- Base URL: `https://api.surplify.de/api/v1/`\n- Authentication via **API key** (create in the developer portal)\n- Per-key rate limits\n- OpenAPI spec available\n\nThe interactive API reference is under **\"Developers\" > API Documentation**."
        },
        {
          "title": "Outbound webhooks (HMAC signed)",
          "content": "Receive real-time events in your systems:\n\n1. Create a webhook endpoint in the developer portal\n2. Subscribe to events (e.g. `material.created`, `transaction.completed`, `offer.accepted`)\n3. Every payload is **HMAC-SHA256 signed** (header `X-Surplify-Signature`)\n4. Automatic retries with exponential backoff on delivery failures\n\nValidate signatures using the algorithm documented in the API docs to prevent tampering."
        },
        {
          "title": "Client SDKs (Python, Node.js, PHP)",
          "content": "Dynamically generated SDKs are available for download:\n\n- **Python SDK** (pip package)\n- **Node.js SDK** (npm package)\n- **PHP SDK** (Composer)\n\nThe SDKs come preconfigured with auth handling, rate-limit retries and typed models. Download them in the developer portal."
        },
        {
          "title": "CLI: npx create-surplify-app",
          "content": "The official **scaffolding tool** gets you started in seconds:\n\n```bash\nnpx create-surplify-app my-integration\ncd my-integration\nnpm install\nnpm run dev\n```\n\nThe generated project includes:\n- Preconfigured webhook listener with HMAC validation\n- Example API calls (list materials, send requests)\n- `.env` template for API key, webhook secret and base URL\n- Tests and hot-reload\n\nPerfect to build integrations in minutes instead of days."
        }
      ]
    },
    {
      "id": "adminOverview",
      "title": "Admin Portal Overview",
      "sections": [
        {
          "title": "What can a platform admin do?",
          "content": "Platform admins have access to all administrative layers:\n\n- **Users & roles:** create, suspend, change roles\n- **Subscriptions:** manual adjustments, goodwill credits\n- **Commissions:** create and version models\n- **SEPA mandates:** inspect and approve\n- **Invoices:** all invoices platform-wide\n- **DATEV export:** generate accounting data\n- **Scenarios:** steer global platform behavior\n- **Audit log:** track every security-relevant action"
        }
      ]
    }
  ],

  "guide.faq": [
    {
      "category": "General",
      "questions": [
        { "q": "What is Surplify?", "a": "Surplify is a B2B marketplace for packaging materials. Companies can list surplus materials or search for materials they need." },
        { "q": "Who can use Surplify?", "a": "Surplify is aimed at companies in the packaging industry — traders, manufacturers and processors. Both small and large companies benefit from the platform." },
        { "q": "Is it free to use?", "a": "Registration is free. For advanced features like more listings, matching or API access, paid tiers are available." },
        { "q": "In which languages is Surplify available?", "a": "Seven languages: German, English, Spanish, French, Italian, Portuguese and Romanian. Switch via the globe in the header." }
      ]
    },
    {
      "category": "Account & Registration",
      "questions": [
        { "q": "How do I create an account?", "a": "Click \"Register\", fill the form and confirm your email address. You can start right away." },
        { "q": "Can I change my email address?", "a": "Contact support to change your email address. For security reasons this can't be done by yourself." },
        { "q": "What if I forget my password?", "a": "Use the \"Forgot password\" feature on the login page. You'll receive an email with a reset link." },
        { "q": "What roles are there?", "a": "Trader, Manager (admin of a site), Group admin (manages multiple sites), Platform admin and external suppliers. The role determines which menu items are visible." }
      ]
    },
    {
      "category": "Trading & Materials",
      "questions": [
        { "q": "How do I list a material?", "a": "Click \"List\", fill the form with all relevant details and publish. It appears on the marketplace instantly." },
        { "q": "How does matching work?", "a": "The matching engine automatically compares listings and wanted posts based on material type, quantity and location. Both parties are notified on a match." },
        { "q": "Can I bulk import materials?", "a": "Yes, managers and admins can use the Import feature to list multiple materials at once via Excel upload (with validation and dry-run)." },
        { "q": "What are wanted posts?", "a": "Wanted posts are active buying requests. When you need a certain material, create a wanted post. Sellers can respond with offers." },
        { "q": "What's the watchlist for?", "a": "The watchlist stores materials you want to keep an eye on without sending a request. You can optionally receive alerts on price or status changes." }
      ]
    },
    {
      "category": "Messages & Offers",
      "questions": [
        { "q": "How do I send a binding offer?", "a": "Open the thread for the transaction, click \"Send offer\" and enter price, quantity and conditions. The offer is highlighted in the chat history." },
        { "q": "What happens when an offer is accepted?", "a": "The transaction is auto-updated to the agreed price and both parties receive an email confirmation with all details (quantity, price, counterparty contact)." },
        { "q": "Can I withdraw an offer?", "a": "Yes, as long as the recipient hasn't accepted yet, you can click \"Withdraw\" in the thread. Once accepted, the offer is binding." },
        { "q": "How does a counter-offer work?", "a": "Simply send a new offer with different terms. The previous one stays visible in history, but only the most recently accepted offer is binding." }
      ]
    },
    {
      "category": "Payments & Invoices",
      "questions": [
        { "q": "Which payment methods are supported?", "a": "Payments run via Stripe. Credit card (Visa, Mastercard, Amex) and SEPA direct debit are supported." },
        { "q": "Where do I find my invoices?", "a": "In the Seller Portal under \"Invoices\" or in the Group Admin Portal. All invoices can be downloaded as PDF." },
        { "q": "What is a SEPA mandate?", "a": "The SEPA mandate authorizes Surplify to collect payments from your bank account. You can generate it in settings after storing your IBAN/BIC." },
        { "q": "How is my VAT ID validated?", "a": "Surplify automatically validates your VAT ID via the EU VIES system. Enter it in the settings of your portal." },
        { "q": "Is reverse-charge applied?", "a": "Yes, automatically for a valid EU VAT ID outside Germany. The invoice then includes the corresponding note." }
      ]
    },
    {
      "category": "Groups & Sites",
      "questions": [
        { "q": "What is a company group?", "a": "A company group bundles multiple sites of one organization. The group is managed and billed centrally." },
        { "q": "How do I invite sites to my group?", "a": "In the Group Admin Portal under \"Invitations\". You can invite by email or via site search. Invitations expire after a while." },
        { "q": "Can a site belong to multiple groups?", "a": "No, a site can only belong to one group at a time." }
      ]
    },
    {
      "category": "External Portal",
      "questions": [
        { "q": "Who is the external portal for?", "a": "For occasional external partners who want to respond to requests or submit offers without a full account." },
        { "q": "How do I register as an external supplier?", "a": "Via an invitation link from a manager or the public registration page for external providers." },
        { "q": "Can I upgrade from external to a full account?", "a": "Yes, upgrading is possible — contact support or the inviting manager." }
      ]
    },
    {
      "category": "Administration",
      "questions": [
        { "q": "What can a platform admin do?", "a": "User management, subscription management, commissions, SEPA mandates, invoices, DATEV export, scenarios, audit log and all analytical dashboards." },
        { "q": "What is the DATEV export?", "a": "The DATEV export generates booking data in DATEV format for accounting. It's available under the Admin Dashboard (Exports tab)." },
        { "q": "How do I manage commissions?", "a": "In the Commission Dashboard admins can configure commission models, review transactions and create settlements." },
        { "q": "What's the audit log?", "a": "An immutable log of every security-relevant action (logins, role changes, invoice adjustments) — available in the Admin Dashboard." }
      ]
    },
    {
      "category": "Developer & API",
      "questions": [
        { "q": "How do I get an API key?", "a": "In the Developer Portal under \"API Keys\" you can create a new key. Store it securely — it's shown only once." },
        { "q": "Are there rate limits?", "a": "Yes, per API key. Exact limits are visible in the Developer Portal and depend on your tier. On exceed you get HTTP 429." },
        { "q": "How do I validate webhook signatures?", "a": "Every payload is signed with HMAC-SHA256 (header X-Surplify-Signature). Use your webhook secret to validate — code samples are in the API docs." },
        { "q": "What is create-surplify-app?", "a": "An official scaffolding tool (npx create-surplify-app my-integration) that creates a preconfigured integration project with webhook listener, API client and tests in seconds." },
        { "q": "Which SDKs are available?", "a": "Python, Node.js and PHP. All are dynamically generated and include auth handling, retries and typed models." }
      ]
    },
    {
      "category": "Support & Languages",
      "questions": [
        { "q": "How do I report a bug?", "a": "Click the bug icon in the header or open \"My Bug Reports\". Describe the issue and optionally attach a screenshot." },
        { "q": "How do I change the language?", "a": "At the top right in the header you'll find the globe — click it and select your language. Your choice is saved." },
        { "q": "How do I contact support?", "a": "For urgent matters use the contact details in the imprint or write directly from the portal (\"Help > Contact Support\")." }
      ]
    }
  ]
};

export const guideContentES = {
  "guide.title": "Ayuda y preguntas frecuentes",
  "guide.subtitle": "Manual de usuario y respuestas a las preguntas más frecuentes",
  "guide.searchPlaceholder": "Buscar en el manual y las preguntas frecuentes...",
  "guide.tabGuide": "Manual de usuario",
  "guide.tabFaq": "Preguntas frecuentes",
  "guide.noResults": "Sin resultados para",
  "guide.topic": "tema",
  "guide.topics": "temas",
  "guide.vote.helpful": "¿Te resultó útil este artículo?",
  "guide.vote.up": "Útil",
  "guide.vote.down": "No útil",
  "guide.vote.thanksUp": "¡Gracias por tu opinión!",
  "guide.vote.thanksDown": "Gracias — mejoraremos el artículo.",
  "guide.vote.failed": "Error al enviar la opinión",

  "guide.categories": [
    {
      "id": "gettingStarted",
      "title": "Primeros pasos",
      "sections": [
        {
          "title": "Registro e inicio de sesión",
          "content": "Para usar Surplify, regístrate con tu correo electrónico profesional. Tras registrarte, accederás a tu panel personal.\n\n**Cómo registrarse:**\n1. Haz clic en \"Registrarse\" en la parte superior derecha\n2. Introduce tu nombre, correo electrónico y contraseña\n3. Elige tu rol (Comerciante, Gerente, etc.)\n4. Tras el registro, accederás automáticamente\n\n**Inicio de sesión:** Usa tu correo registrado y tu contraseña en la página de inicio de sesión."
        },
        {
          "title": "Resumen de la navegación",
          "content": "El menú principal está arriba a la derecha, bajo tu nombre de usuario. Está agrupado en:\n\n- **Mercado** — tus artículos, lista de seguimiento, búsquedas, materiales estándar\n- **Comercio y comunicación** — solicitudes, transacciones, emparejamiento, mensajes\n- **Administración** — panel, analítica, importación (solo gerentes/administradores)\n- **Portales** — portal de vendedor, portal de administrador de grupo, portal externo\n- **Desarrolladores** — API, webhooks, SDK, CLI\n- **Ajustes** — notificaciones, ayuda, idioma"
        },
        {
          "title": "Cambiar el idioma",
          "content": "Surplify está disponible en varios idiomas (alemán, inglés, español, francés, italiano, portugués, rumano).\n\n**Para cambiar de idioma:**\n1. Haz clic en el selector de idioma (icono de globo) en la cabecera\n2. Selecciona el idioma deseado\n3. Toda la interfaz se traduce al instante\n\nTu elección se guarda en el navegador y se restaura en tu próxima visita."
        }
      ]
    },
    {
      "id": "marketplace",
      "title": "Mercado",
      "sections": [
        {
          "title": "Publicar un material",
          "content": "Para publicar un material en el mercado:\n\n1. Haz clic en **\"Publicar\"** en la navegación superior\n2. Rellena el formulario: nombre, categoría, descripción, cantidad, precio\n3. Añade imágenes y especificaciones opcionales\n4. Haz clic en \"Publicar\"\n\n**Consejos:** Las descripciones detalladas y las imágenes aumentan la visibilidad."
        },
        {
          "title": "Buscar y encontrar materiales",
          "content": "Usa la barra de búsqueda central para encontrar materiales. Puedes buscar por:\n\n- **Nombre del material** o **categoría**\n- **Filtros:** tipo, precio, cantidad, ubicación\n- Ordenar resultados por relevancia, precio o fecha\n\nLa paginación y la ordenación (fecha, precio, cantidad) están disponibles para grandes conjuntos de resultados."
        },
        {
          "title": "Lista de seguimiento",
          "content": "La **lista de seguimiento** te permite vigilar materiales interesantes sin consultar de inmediato.\n\n**Cómo funciona:**\n1. Haz clic en el **icono de corazón** en una tarjeta o página de detalle de un material\n2. El material se añade a tu lista de seguimiento personal\n3. Abre \"Lista de seguimiento\" en el menú para ver todos los artículos guardados\n\n**Notificaciones:** Puedes optar por recibir avisos cuando baje el precio o cambie el estado de un material seguido."
        },
        {
          "title": "Crear búsquedas (anuncios de demanda)",
          "content": "En **\"Búsquedas\"** puedes publicar solicitudes de compra activas:\n\n1. Ve a Búsquedas\n2. Crea una nueva búsqueda con tipo, cantidad y presupuesto\n3. Los vendedores pueden ver tus búsquedas y responder\n\nLas búsquedas activas también son consideradas por el motor de emparejamiento."
        },
        {
          "title": "Materiales estándar",
          "content": "La lista de materiales estándar contiene tipos de materiales predefinidos para tu organización. Sirven de plantilla al crear anuncios para que el inventario se mantenga coherente. Los gerentes pueden mantener la lista de forma centralizada."
        }
      ]
    },
    {
      "id": "trading",
      "title": "Comercio y transacciones",
      "sections": [
        {
          "title": "Enviar y gestionar solicitudes",
          "content": "Si te interesa un material:\n\n1. Abre la página de detalle del material\n2. Haz clic en **\"Enviar solicitud\"**\n3. Introduce la cantidad deseada y un comentario opcional\n4. La solicitud se envía al vendedor\n\nLas **solicitudes entrantes** están en \"Solicitudes\" de tu menú con filtros de estado (Abierta, Aceptada, Rechazada)."
        },
        {
          "title": "Transacciones",
          "content": "En **\"Transacciones\"** ves todas las operaciones en curso y cerradas:\n\n- **Estado:** Abierta, En negociación, Completada, Cancelada\n- **Detalles:** cantidad, precio, datos del comprador/vendedor, adjuntos\n- Cada transacción se puede comentar y genera automáticamente un hilo de mensajes"
        },
        {
          "title": "Emparejamiento inteligente",
          "content": "El **motor de emparejamiento** compara automáticamente tus anuncios con las búsquedas y viceversa:\n\n- Los emparejamientos se calculan en tiempo real (tipo de material, cantidad, región, rango de precio)\n- Recibes avisos por correo sobre nuevos emparejamientos\n- Haz clic en un emparejamiento para iniciar directamente una solicitud o un mensaje\n\nEl panel de emparejamiento (gerente/administrador) muestra la tasa de aciertos y los mejores emparejamientos."
        }
      ]
    },
    {
      "id": "messaging",
      "title": "Mensajes y ofertas",
      "sections": [
        {
          "title": "Hilos de mensajes",
          "content": "El **sistema de mensajería** integrado permite la comunicación directa 1:1 entre comprador y vendedor:\n\n- Cada transacción genera un hilo automáticamente\n- Los mensajes no leídos se muestran como una insignia en el menú\n- Puedes adjuntar archivos (especificaciones, fotos) a los mensajes"
        },
        {
          "title": "Enviar ofertas",
          "content": "En cualquier hilo puedes enviar una **oferta vinculante**:\n\n1. Abre el hilo\n2. Haz clic en **\"Enviar oferta\"**\n3. Introduce precio, cantidad y condiciones opcionales (plazo de recogida, condiciones de pago)\n4. La oferta aparece como un mensaje destacado con los botones \"Aceptar\" / \"Rechazar\"\n\nLas ofertas tienen un estado claro: **Abierta**, **Aceptada**, **Rechazada** o **Retirada**."
        },
        {
          "title": "Aceptar o rechazar una oferta",
          "content": "Como destinatario de una oferta:\n\n- **Aceptar:** Haz clic en \"Aceptar\". La transacción se actualiza automáticamente al precio acordado y ambas partes reciben una **confirmación automática por correo** con todos los datos (cantidad, precio, contraparte, contacto).\n- **Rechazar:** Haz clic en \"Rechazar\". La oferta se marca como rechazada. Puedes enviar una contraoferta.\n\nTodas las acciones quedan registradas en el historial del hilo."
        },
        {
          "title": "Contraofertas y negociación",
          "content": "Puedes enviar una **contraoferta** en cualquier momento simplemente enviando una nueva oferta con condiciones diferentes. La oferta anterior permanece visible en el historial, pero solo la última oferta aceptada es vinculante."
        }
      ]
    },
    {
      "id": "subscriptions",
      "title": "Suscripciones y pagos",
      "sections": [
        {
          "title": "Planes y niveles",
          "content": "Surplify ofrece varios niveles de suscripción:\n\n- **Basic** — funciones esenciales para empezar\n- **Professional** — funciones avanzadas, más anuncios\n- **Enterprise** — conjunto completo de funciones, incl. funcionalidad de grupo y acceso API\n\nLas mejoras y reducciones de plan están disponibles en cualquier momento desde el **Portal de vendedor** con facturación prorrateada automática."
        },
        {
          "title": "Pagos con Stripe",
          "content": "Todos los pagos se procesan de forma segura a través de **Stripe**:\n\n1. Elige el nivel deseado\n2. Se te redirige a Stripe Checkout\n3. Tras el pago exitoso, tu suscripción se activa al instante\n\nMétodos admitidos: tarjeta de crédito (Visa, Mastercard, Amex) y domiciliación SEPA. Las **facturas** están en el Portal de vendedor, en \"Facturas\"."
        },
        {
          "title": "Mandato de domiciliación SEPA",
          "content": "Para los pagos SEPA puedes guardar tu **IBAN y BIC**:\n\n1. Ve a tu Portal (vendedor o administrador de grupo)\n2. En \"Ajustes\" encontrarás los datos de pago\n3. Introduce el IBAN y el BIC\n4. Descarga tu mandato SEPA en PDF\n\nEl mandato se genera automáticamente con los datos actuales de tu empresa y cumple plenamente con SEPA."
        },
        {
          "title": "Facturas y NIF-IVA",
          "content": "- Las **facturas** se generan automáticamente en PDF y se pueden descargar en cualquier momento\n- La **validación del NIF-IVA** se realiza automáticamente a través del sistema VIES de la UE\n- La **dirección de facturación** se puede editar en los ajustes de tu portal\n- La **inversión del sujeto pasivo** se aplica automáticamente cuando se almacena un NIF-IVA de la UE válido fuera de Alemania"
        }
      ]
    },
    {
      "id": "groups",
      "title": "Grupos e invitaciones",
      "sections": [
        {
          "title": "Concepto de grupo",
          "content": "Los **grupos de empresa** te permiten gestionar varias sedes bajo un mismo paraguas:\n\n- Un **administrador de grupo** gestiona todo el grupo\n- Las **sedes** individuales son miembros del grupo\n- Facturación compartida y administración central\n- Centros de coste opcionales por sede"
        },
        {
          "title": "Invitaciones",
          "content": "Como administrador de grupo puedes invitar a nuevos miembros:\n\n1. Ve al **Portal de administrador de grupo** > \"Invitaciones\"\n2. Elige \"Invitar por correo\" o \"Invitar por sede\"\n3. El invitado recibe un correo con un enlace\n4. Al aceptar, la sede se une al grupo\n\n**Estado:** Pendiente, Aceptada, Rechazada, Caducada, Revocada"
        },
        {
          "title": "Portal de administrador de grupo",
          "content": "El Portal de administrador de grupo ofrece:\n\n- **Resumen:** tarjetas de KPI, estado del grupo, detalles del nivel\n- **Miembros:** lista de todas las sedes con edición de centros de coste\n- **Invitaciones:** enviar y gestionar invitaciones\n- **Facturas:** facturas del grupo en PDF\n- **Ajustes:** dirección de facturación, NIF-IVA, datos SEPA"
        }
      ]
    },
    {
      "id": "sellerPortal",
      "title": "Portal de vendedor",
      "sections": [
        {
          "title": "Resumen",
          "content": "En el **Portal de vendedor** gestionas tu suscripción y todos los detalles financieros:\n\n- **Suscripción:** estado, detalles del nivel, mejora/reducción, cancelación\n- **Facturas:** todas las facturas con descarga en PDF\n- **Comisiones:** resumen de ingresos por comisiones e historial de transacciones\n- **Invitaciones:** aceptar o rechazar invitaciones de grupo pendientes\n- **Ajustes:** dirección de facturación, NIF-IVA, número de pedido, datos SEPA"
        }
      ]
    },
    {
      "id": "externalPortal",
      "title": "Portal externo de proveedores",
      "sections": [
        {
          "title": "¿Qué es el portal externo?",
          "content": "El **portal externo** es un área dedicada a proveedores o procesadores externos que quieren responder a solicitudes sin una cuenta completa de Surplify.\n\n- Conjunto de funciones limitado: ver solicitudes, enviar ofertas, intercambiar mensajes\n- Sin visibilidad de materiales o grupos internos\n- Ideal para socios ocasionales"
        },
        {
          "title": "Registro externo",
          "content": "Los usuarios externos pueden registrarse a través de una **página de invitación pública** o mediante un enlace de invitación enviado por un gerente. Tras el registro, la cuenta es utilizable de inmediato, pero limitada a las vistas externas."
        },
        {
          "title": "Escenarios y acceso externo",
          "content": "Los administradores pueden controlar si las empresas externas pueden acceder a las solicitudes y cuándo, mediante **escenarios**. El ajuste \"External Enabled\" en la configuración del escenario habilita o bloquea el acceso externo de forma global."
        }
      ]
    },
    {
      "id": "commissions",
      "title": "Comisiones",
      "sections": [
        {
          "title": "¿Cómo funcionan las comisiones?",
          "content": "Por cada transacción cerrada con éxito, Surplify cobra una **comisión porcentual** sobre el precio de venta. La tarifa depende del nivel de suscripción:\n\n- Basic: tarifa estándar\n- Professional: tarifa reducida\n- Enterprise: negociable individualmente"
        },
        {
          "title": "Panel de comisiones",
          "content": "En **\"Comisiones\"** puedes ver:\n\n- Modelos de comisión actuales (activos/históricos)\n- Historial de transacciones con la comisión calculada por operación\n- Totales mensuales y cifras anuales acumuladas\n- Estado de vencimiento y liquidaciones\n\nLos administradores pueden crear, activar y versionar modelos de comisión en el panel de administración."
        }
      ]
    },
    {
      "id": "analytics",
      "title": "Analítica",
      "sections": [
        {
          "title": "Panel de analítica",
          "content": "El **panel de analítica** ofrece a gerentes y administradores información sobre:\n\n- Materiales destacados (visualizaciones, solicitudes, conversiones)\n- Tendencias de ingresos y transacciones\n- Tasas de acierto de emparejamiento\n- Actividad de usuarios por sede/grupo\n\nTodos los gráficos se pueden filtrar por periodo (7/30/90 días, personalizado)."
        },
        {
          "title": "Exportación e informes",
          "content": "Los resultados de la analítica se pueden exportar como **CSV o Excel**. Para contabilidad también está disponible la **exportación DATEV** en el panel de administración."
        }
      ]
    },
    {
      "id": "importing",
      "title": "Importación masiva (CSV/Excel)",
      "sections": [
        {
          "title": "Publicar materiales mediante carga",
          "content": "Los gerentes y administradores pueden importar varios materiales a la vez:\n\n1. Ve a **\"Importar\"**\n2. Descarga la **plantilla de Excel** (muestra el formato de columnas esperado)\n3. Rellena la plantilla con tus datos\n4. Sube el archivo\n5. Revisa los resultados de validación (los errores se resaltan por fila)\n6. Elige un modo de importación:\n   - **Sobrescribir todo:** reemplaza los artículos existentes\n   - **Solo enriquecer:** añade campos que faltan sin sobrescribir los existentes\n   - **Seleccionar campos:** control detallado de las columnas actualizadas\n7. Ejecuta la importación"
        }
      ]
    },
    {
      "id": "scenarios",
      "title": "Escenarios (administrador)",
      "sections": [
        {
          "title": "Modos de escenario",
          "content": "Los administradores dirigen el comportamiento global de la plataforma mediante **escenarios**:\n\n- **Equilibrado (predeterminado):** reglas estándar para la visibilidad interna y externa\n- **Conservador:** mayor visibilidad interna, aprobación más estricta\n- **Aggressive Cash:** externalización rápida, máximo alcance\n- **Modo crisis:** acceso externo bloqueado, solo operaciones internas\n\nParámetros como la \"ventana interna\" (días de visibilidad solo interna) y los \"días de escalado\" (notificación automática para solicitudes antiguas) son configurables."
        }
      ]
    },
    {
      "id": "bugReports",
      "title": "Notificar errores",
      "sections": [
        {
          "title": "Enviar un informe de error",
          "content": "Si detectas un error:\n\n1. Haz clic en el **icono de insecto** de la cabecera o abre **\"Mis informes de error\"**\n2. Describe el problema con la mayor precisión posible (URL, pasos, comportamiento esperado)\n3. Adjunta una captura de pantalla (opcional pero recomendado)\n4. Envía\n\nEl seguimiento de estado muestra si el equipo ha confirmado (\"Abierto\"), está trabajando (\"En curso\") o ha resuelto (\"Hecho\") el informe."
        }
      ]
    },
    {
      "id": "notifications",
      "title": "Notificaciones",
      "sections": [
        {
          "title": "Configurar notificaciones por correo",
          "content": "Surplify envía correos automáticos para eventos clave:\n\n- Nuevas solicitudes y ofertas\n- **Aceptación de oferta** (comprador + vendedor reciben una confirmación cada uno)\n- Aciertos de emparejamiento\n- Pagos y recordatorios\n- Invitaciones y cambios de grupo\n- Escalados (solicitudes antiguas sin atender)\n\nEn **\"Notificaciones por correo\"** de los ajustes puedes activar o desactivar cada tipo de evento individualmente."
        }
      ]
    },
    {
      "id": "developerApi",
      "title": "Desarrollador: API, webhooks, SDK y CLI",
      "sections": [
        {
          "title": "API REST pública",
          "content": "Surplify ofrece una **API REST** completa (v1 y v2) para la integración en tus sistemas ERP o de logística:\n\n- URL base: `https://api.surplify.de/api/v1/`\n- Autenticación mediante **clave API** (créala en el portal de desarrolladores)\n- Límites de tasa por clave\n- Especificación OpenAPI disponible\n\nLa referencia interactiva de la API está en **\"Desarrolladores\" > Documentación de la API**."
        },
        {
          "title": "Webhooks salientes (firmados con HMAC)",
          "content": "Recibe eventos en tiempo real en tus sistemas:\n\n1. Crea un endpoint de webhook en el portal de desarrolladores\n2. Suscríbete a eventos (p. ej. `material.created`, `transaction.completed`, `offer.accepted`)\n3. Cada payload está **firmado con HMAC-SHA256** (cabecera `X-Surplify-Signature`)\n4. Reintentos automáticos con retroceso exponencial ante fallos de entrega\n\nValida las firmas con el algoritmo documentado en la API para evitar manipulaciones."
        },
        {
          "title": "SDK de cliente (Python, Node.js, PHP)",
          "content": "Hay SDK generados dinámicamente disponibles para descargar:\n\n- **SDK de Python** (paquete pip)\n- **SDK de Node.js** (paquete npm)\n- **SDK de PHP** (Composer)\n\nLos SDK vienen preconfigurados con gestión de autenticación, reintentos por límite de tasa y modelos tipados. Descárgalos en el portal de desarrolladores."
        },
        {
          "title": "CLI: npx create-surplify-app",
          "content": "La **herramienta oficial de scaffolding** te permite empezar en segundos:\n\n```bash\nnpx create-surplify-app my-integration\ncd my-integration\nnpm install\nnpm run dev\n```\n\nEl proyecto generado incluye:\n- Listener de webhook preconfigurado con validación HMAC\n- Ejemplos de llamadas API (listar materiales, enviar solicitudes)\n- Plantilla `.env` para la clave API, el secreto del webhook y la URL base\n- Pruebas y recarga en caliente\n\nPerfecto para crear integraciones en minutos en lugar de días."
        }
      ]
    },
    {
      "id": "adminOverview",
      "title": "Resumen del portal de administración",
      "sections": [
        {
          "title": "¿Qué puede hacer un administrador de plataforma?",
          "content": "Los administradores de plataforma tienen acceso a todas las capas administrativas:\n\n- **Usuarios y roles:** crear, suspender, cambiar roles\n- **Suscripciones:** ajustes manuales, créditos de buena voluntad\n- **Comisiones:** crear y versionar modelos\n- **Mandatos SEPA:** inspeccionar y aprobar\n- **Facturas:** todas las facturas de la plataforma\n- **Exportación DATEV:** generar datos contables\n- **Escenarios:** dirigir el comportamiento global de la plataforma\n- **Registro de auditoría:** seguir cada acción relevante para la seguridad"
        }
      ]
    }
  ],

  "guide.faq": [
    {
      "category": "General",
      "questions": [
        { "q": "¿Qué es Surplify?", "a": "Surplify es un mercado B2B de materiales de embalaje. Las empresas pueden publicar materiales excedentes o buscar los materiales que necesitan." },
        { "q": "¿Quién puede usar Surplify?", "a": "Surplify está dirigido a empresas del sector del embalaje: comerciantes, fabricantes y procesadores. Tanto las pequeñas como las grandes empresas se benefician de la plataforma." },
        { "q": "¿Es gratuito su uso?", "a": "El registro es gratuito. Para funciones avanzadas como más anuncios, emparejamiento o acceso API hay niveles de pago disponibles." },
        { "q": "¿En qué idiomas está disponible Surplify?", "a": "Siete idiomas: alemán, inglés, español, francés, italiano, portugués y rumano. Cambia mediante el globo de la cabecera." }
      ]
    },
    {
      "category": "Cuenta y registro",
      "questions": [
        { "q": "¿Cómo creo una cuenta?", "a": "Haz clic en \"Registrarse\", rellena el formulario y confirma tu correo electrónico. Puedes empezar enseguida." },
        { "q": "¿Puedo cambiar mi correo electrónico?", "a": "Contacta con el soporte para cambiar tu correo. Por motivos de seguridad no se puede hacer uno mismo." },
        { "q": "¿Qué pasa si olvido mi contraseña?", "a": "Usa la función \"Olvidé mi contraseña\" en la página de inicio de sesión. Recibirás un correo con un enlace de restablecimiento." },
        { "q": "¿Qué roles existen?", "a": "Comerciante, Gerente (administrador de una sede), Administrador de grupo (gestiona varias sedes), Administrador de plataforma y proveedores externos. El rol determina qué elementos del menú son visibles." }
      ]
    },
    {
      "category": "Comercio y materiales",
      "questions": [
        { "q": "¿Cómo publico un material?", "a": "Haz clic en \"Publicar\", rellena el formulario con todos los detalles relevantes y publica. Aparece en el mercado al instante." },
        { "q": "¿Cómo funciona el emparejamiento?", "a": "El motor de emparejamiento compara automáticamente los anuncios y las búsquedas según el tipo de material, la cantidad y la ubicación. Ambas partes son notificadas cuando hay una coincidencia." },
        { "q": "¿Puedo importar materiales de forma masiva?", "a": "Sí, los gerentes y administradores pueden usar la función de importación para publicar varios materiales a la vez mediante carga de Excel (con validación y prueba en seco)." },
        { "q": "¿Qué son las búsquedas?", "a": "Las búsquedas son solicitudes de compra activas. Cuando necesitas un material concreto, creas una búsqueda. Los vendedores pueden responder con ofertas." },
        { "q": "¿Para qué sirve la lista de seguimiento?", "a": "La lista de seguimiento guarda los materiales que quieres vigilar sin enviar una solicitud. Puedes recibir avisos opcionales sobre cambios de precio o estado." }
      ]
    },
    {
      "category": "Mensajes y ofertas",
      "questions": [
        { "q": "¿Cómo envío una oferta vinculante?", "a": "Abre el hilo de mensajes de la transacción, haz clic en \"Enviar oferta\" e introduce precio, cantidad y condiciones. La oferta aparece destacada en el historial del chat." },
        { "q": "¿Qué pasa cuando se acepta una oferta?", "a": "La transacción se actualiza automáticamente al precio acordado y ambas partes reciben una confirmación por correo con todos los detalles (cantidad, precio, contacto de la contraparte)." },
        { "q": "¿Puedo retirar una oferta?", "a": "Sí, mientras el destinatario no la haya aceptado, puedes hacer clic en \"Retirar\" en el hilo. Una vez aceptada, la oferta es vinculante." },
        { "q": "¿Cómo funciona una contraoferta?", "a": "Simplemente envía una nueva oferta con condiciones diferentes. La anterior permanece visible en el historial, pero solo la última oferta aceptada es vinculante." }
      ]
    },
    {
      "category": "Pagos y facturas",
      "questions": [
        { "q": "¿Qué métodos de pago se admiten?", "a": "Los pagos se procesan a través de Stripe. Se admiten tarjeta de crédito (Visa, Mastercard, Amex) y domiciliación SEPA." },
        { "q": "¿Dónde encuentro mis facturas?", "a": "En el Portal de vendedor, en \"Facturas\", o en el Portal de administrador de grupo. Todas las facturas se pueden descargar en PDF." },
        { "q": "¿Qué es un mandato SEPA?", "a": "El mandato SEPA autoriza a Surplify a cobrar pagos de tu cuenta bancaria. Puedes generarlo en los ajustes tras guardar tu IBAN/BIC." },
        { "q": "¿Cómo se valida mi NIF-IVA?", "a": "Surplify valida automáticamente tu NIF-IVA a través del sistema VIES de la UE. Introdúcelo en los ajustes de tu portal." },
        { "q": "¿Se aplica la inversión del sujeto pasivo?", "a": "Sí, automáticamente para un NIF-IVA de la UE válido fuera de Alemania. La factura incluye entonces la nota correspondiente." }
      ]
    },
    {
      "category": "Grupos y sedes",
      "questions": [
        { "q": "¿Qué es un grupo de empresa?", "a": "Un grupo de empresa agrupa varias sedes de una misma organización. El grupo se gestiona y se factura de forma centralizada." },
        { "q": "¿Cómo invito sedes a mi grupo?", "a": "En el Portal de administrador de grupo, en \"Invitaciones\". Puedes invitar por correo o mediante búsqueda de sedes. Las invitaciones caducan tras un tiempo." },
        { "q": "¿Puede una sede pertenecer a varios grupos?", "a": "No, una sede solo puede pertenecer a un grupo a la vez." }
      ]
    },
    {
      "category": "Portal externo",
      "questions": [
        { "q": "¿Para quién es el portal externo?", "a": "Para socios externos ocasionales que quieren responder a solicitudes o enviar ofertas sin una cuenta completa." },
        { "q": "¿Cómo me registro como proveedor externo?", "a": "Mediante un enlace de invitación de un gerente o la página de registro pública para proveedores externos." },
        { "q": "¿Puedo pasar de una cuenta externa a una completa?", "a": "Sí, la mejora es posible: contacta con el soporte o con el gerente que te invitó." }
      ]
    },
    {
      "category": "Administración",
      "questions": [
        { "q": "¿Qué puede hacer un administrador de plataforma?", "a": "Gestión de usuarios, gestión de suscripciones, comisiones, mandatos SEPA, facturas, exportación DATEV, escenarios, registro de auditoría y todos los paneles analíticos." },
        { "q": "¿Qué es la exportación DATEV?", "a": "La exportación DATEV genera datos contables en formato DATEV para la asesoría fiscal. Está disponible en el panel de administración (pestaña Exportaciones)." },
        { "q": "¿Cómo gestiono las comisiones?", "a": "En el panel de comisiones los administradores pueden configurar modelos de comisión, revisar transacciones y crear liquidaciones." },
        { "q": "¿Qué es el registro de auditoría?", "a": "Un registro inmutable de cada acción relevante para la seguridad (inicios de sesión, cambios de rol, ajustes de facturas), disponible en el panel de administración." }
      ]
    },
    {
      "category": "Desarrollador y API",
      "questions": [
        { "q": "¿Cómo obtengo una clave API?", "a": "En el portal de desarrolladores, en \"Claves API\", puedes crear una nueva clave. Guárdala de forma segura: solo se muestra una vez." },
        { "q": "¿Hay límites de tasa?", "a": "Sí, por clave API. Los límites exactos se ven en el portal de desarrolladores y dependen de tu nivel. Al superarlos recibes un HTTP 429." },
        { "q": "¿Cómo valido las firmas de webhook?", "a": "Cada payload se firma con HMAC-SHA256 (cabecera X-Surplify-Signature). Usa tu secreto de webhook para validar; hay ejemplos de código en la documentación de la API." },
        { "q": "¿Qué es create-surplify-app?", "a": "Una herramienta oficial de scaffolding (npx create-surplify-app my-integration) que crea un proyecto de integración preconfigurado con listener de webhook, cliente API y pruebas en segundos." },
        { "q": "¿Qué SDK hay disponibles?", "a": "Python, Node.js y PHP. Todos se generan dinámicamente e incluyen gestión de autenticación, reintentos y modelos tipados." }
      ]
    },
    {
      "category": "Soporte e idiomas",
      "questions": [
        { "q": "¿Cómo notifico un error?", "a": "Haz clic en el icono de insecto de la cabecera o abre \"Mis informes de error\". Describe el problema y adjunta opcionalmente una captura de pantalla." },
        { "q": "¿Cómo cambio el idioma?", "a": "Arriba a la derecha en la cabecera encontrarás el globo: haz clic en él y selecciona tu idioma. Tu elección se guarda." },
        { "q": "¿Cómo contacto con el soporte?", "a": "Para asuntos urgentes usa los datos de contacto del aviso legal o escribe directamente desde el portal (\"Ayuda > Contactar con soporte\")." }
      ]
    }
  ]
};

export const guideContentFR = {
  "guide.title": "Aide et FAQ",
  "guide.subtitle": "Manuel d'utilisation et réponses aux questions fréquentes",
  "guide.searchPlaceholder": "Rechercher dans le manuel et la FAQ...",
  "guide.tabGuide": "Manuel d'utilisation",
  "guide.tabFaq": "FAQ",
  "guide.noResults": "Aucun résultat pour",
  "guide.topic": "sujet",
  "guide.topics": "sujets",
  "guide.vote.helpful": "Cet article vous a-t-il été utile ?",
  "guide.vote.up": "Utile",
  "guide.vote.down": "Pas utile",
  "guide.vote.thanksUp": "Merci pour votre retour !",
  "guide.vote.thanksDown": "Merci — nous améliorerons l'article.",
  "guide.vote.failed": "Échec de l'envoi du retour",

  "guide.categories": [
    {
      "id": "gettingStarted",
      "title": "Premiers pas",
      "sections": [
        {
          "title": "Inscription et connexion",
          "content": "Pour utiliser Surplify, inscrivez-vous avec votre e-mail professionnel. Après l'inscription, vous accédez à votre tableau de bord personnel.\n\n**Comment s'inscrire :**\n1. Cliquez sur « S'inscrire » en haut à droite\n2. Saisissez votre nom, votre e-mail et votre mot de passe\n3. Choisissez votre rôle (négociant, gestionnaire, etc.)\n4. Après l'inscription, vous êtes connecté automatiquement\n\n**Connexion :** utilisez votre e-mail enregistré et votre mot de passe sur la page de connexion."
        },
        {
          "title": "Aperçu de la navigation",
          "content": "Le menu principal se trouve en haut à droite, sous votre nom d'utilisateur. Il est organisé en :\n\n- **Marché** — vos articles, liste de suivi, recherches, matériaux standard\n- **Commerce et communication** — demandes, transactions, mise en relation, messages\n- **Administration** — tableau de bord, analytique, import (gestionnaires/admins uniquement)\n- **Portails** — portail vendeur, portail admin de groupe, portail externe\n- **Développeurs** — API, webhooks, SDK, CLI\n- **Paramètres** — notifications, aide, langue"
        },
        {
          "title": "Changer de langue",
          "content": "Surplify est disponible en plusieurs langues (allemand, anglais, espagnol, français, italien, portugais, roumain).\n\n**Pour changer de langue :**\n1. Cliquez sur le sélecteur de langue (icône globe) dans l'en-tête\n2. Sélectionnez la langue souhaitée\n3. Toute l'interface est traduite instantanément\n\nVotre choix est enregistré dans le navigateur et restauré lors de votre prochaine visite."
        }
      ]
    },
    {
      "id": "marketplace",
      "title": "Marché",
      "sections": [
        {
          "title": "Publier un matériau",
          "content": "Pour publier un matériau sur le marché :\n\n1. Cliquez sur **« Publier »** dans la navigation supérieure\n2. Remplissez le formulaire : nom, catégorie, description, quantité, prix\n3. Ajoutez des images et spécifications optionnelles\n4. Cliquez sur « Publier »\n\n**Conseils :** des descriptions détaillées et des images augmentent la visibilité."
        },
        {
          "title": "Rechercher et trouver des matériaux",
          "content": "Utilisez la barre de recherche centrale pour trouver des matériaux. Vous pouvez rechercher par :\n\n- **Nom du matériau** ou **catégorie**\n- **Filtres :** type, prix, quantité, localisation\n- Trier les résultats par pertinence, prix ou date\n\nLa pagination et le tri (date, prix, quantité) sont disponibles pour les grands ensembles de résultats."
        },
        {
          "title": "Liste de suivi",
          "content": "La **liste de suivi** vous permet de garder un œil sur des matériaux intéressants sans faire de demande immédiate.\n\n**Comment ça marche :**\n1. Cliquez sur l'**icône cœur** sur une fiche ou une page de détail d'un matériau\n2. Le matériau est ajouté à votre liste de suivi personnelle\n3. Ouvrez « Liste de suivi » dans le menu pour voir tous les articles enregistrés\n\n**Notifications :** vous pouvez choisir de recevoir des alertes lorsque le prix baisse ou que le statut d'un matériau suivi change."
        },
        {
          "title": "Créer des recherches (annonces de demande)",
          "content": "Sous **« Recherches »**, vous pouvez publier des demandes d'achat actives :\n\n1. Accédez à Recherches\n2. Créez une nouvelle recherche avec le type, la quantité et le budget\n3. Les vendeurs peuvent voir vos recherches et y répondre\n\nLes recherches actives sont aussi prises en compte par le moteur de mise en relation."
        },
        {
          "title": "Matériaux standard",
          "content": "La liste des matériaux standard contient des types de matériaux prédéfinis pour votre organisation. Ils servent de modèle lors de la création d'annonces afin de garder l'inventaire cohérent. Les gestionnaires peuvent gérer la liste de manière centralisée."
        }
      ]
    },
    {
      "id": "trading",
      "title": "Commerce et transactions",
      "sections": [
        {
          "title": "Envoyer et gérer les demandes",
          "content": "Si un matériau vous intéresse :\n\n1. Ouvrez la page de détail du matériau\n2. Cliquez sur **« Envoyer une demande »**\n3. Saisissez la quantité souhaitée et un commentaire facultatif\n4. La demande est envoyée au vendeur\n\nLes **demandes entrantes** se trouvent sous « Demandes » dans votre menu, avec des filtres de statut (Ouverte, Acceptée, Refusée)."
        },
        {
          "title": "Transactions",
          "content": "Sous **« Transactions »**, vous voyez toutes les opérations en cours et clôturées :\n\n- **Statut :** Ouverte, En négociation, Terminée, Annulée\n- **Détails :** quantité, prix, infos acheteur/vendeur, pièces jointes\n- Chaque transaction peut être commentée et génère automatiquement un fil de messages"
        },
        {
          "title": "Mise en relation intelligente",
          "content": "Le **moteur de mise en relation** compare automatiquement vos annonces avec les recherches et vice-versa :\n\n- Les correspondances sont calculées en temps réel (type de matériau, quantité, région, fourchette de prix)\n- Vous recevez des alertes par e-mail pour les nouvelles correspondances\n- Cliquez sur une correspondance pour démarrer directement une demande ou un message\n\nLe tableau de bord de mise en relation (gestionnaire/admin) affiche le taux de réussite et les meilleures correspondances."
        }
      ]
    },
    {
      "id": "messaging",
      "title": "Messages et offres",
      "sections": [
        {
          "title": "Fils de messages",
          "content": "Le **système de messagerie** intégré permet une communication directe 1:1 entre acheteur et vendeur :\n\n- Chaque transaction génère automatiquement un fil\n- Les messages non lus sont affichés sous forme de badge dans le menu\n- Vous pouvez joindre des fichiers (spécifications, photos) aux messages"
        },
        {
          "title": "Envoyer des offres",
          "content": "Dans tout fil, vous pouvez envoyer une **offre contraignante** :\n\n1. Ouvrez le fil\n2. Cliquez sur **« Envoyer une offre »**\n3. Saisissez le prix, la quantité et des conditions facultatives (délai d'enlèvement, conditions de paiement)\n4. L'offre apparaît comme un message mis en évidence avec les boutons « Accepter » / « Refuser »\n\nLes offres ont un statut clair : **Ouverte**, **Acceptée**, **Refusée** ou **Retirée**."
        },
        {
          "title": "Accepter ou refuser une offre",
          "content": "En tant que destinataire d'une offre :\n\n- **Accepter :** cliquez sur « Accepter ». La transaction est automatiquement mise à jour au prix convenu et les deux parties reçoivent une **confirmation automatique par e-mail** avec tous les détails (quantité, prix, contrepartie, contact).\n- **Refuser :** cliquez sur « Refuser ». L'offre est marquée comme refusée. Vous pouvez envoyer une contre-offre.\n\nToutes les actions sont consignées dans l'historique du fil."
        },
        {
          "title": "Contre-offres et négociation",
          "content": "Vous pouvez envoyer une **contre-offre** à tout moment en soumettant simplement une nouvelle offre avec des conditions différentes. L'offre précédente reste visible dans l'historique, mais seule la dernière offre acceptée est contraignante."
        }
      ]
    },
    {
      "id": "subscriptions",
      "title": "Abonnements et paiements",
      "sections": [
        {
          "title": "Forfaits et niveaux",
          "content": "Surplify propose plusieurs niveaux d'abonnement :\n\n- **Basic** — fonctions essentielles pour démarrer\n- **Professional** — fonctions avancées, plus d'annonces\n- **Enterprise** — ensemble complet de fonctions, dont la fonctionnalité de groupe et l'accès API\n\nLes mises à niveau et rétrogradations sont disponibles à tout moment depuis le **portail vendeur**, avec une facturation au prorata automatique."
        },
        {
          "title": "Paiements via Stripe",
          "content": "Tous les paiements sont traités en toute sécurité via **Stripe** :\n\n1. Choisissez le niveau souhaité\n2. Vous êtes redirigé vers Stripe Checkout\n3. Après un paiement réussi, votre abonnement est activé instantanément\n\nMéthodes prises en charge : carte de crédit (Visa, Mastercard, Amex) et prélèvement SEPA. Les **factures** sont disponibles dans le portail vendeur sous « Factures »."
        },
        {
          "title": "Mandat de prélèvement SEPA",
          "content": "Pour les paiements SEPA, vous pouvez enregistrer votre **IBAN et BIC** :\n\n1. Accédez à votre portail (vendeur ou admin de groupe)\n2. Sous « Paramètres », vous trouverez les coordonnées de paiement\n3. Saisissez l'IBAN et le BIC\n4. Téléchargez votre mandat SEPA au format PDF\n\nLe mandat est généré automatiquement à partir des données actuelles de votre entreprise et est entièrement conforme SEPA."
        },
        {
          "title": "Factures et numéro de TVA",
          "content": "- Les **factures** sont générées automatiquement au format PDF et téléchargeables à tout moment\n- La **validation du numéro de TVA** est effectuée automatiquement via le système VIES de l'UE\n- L'**adresse de facturation** peut être modifiée dans les paramètres de votre portail\n- L'**autoliquidation** est appliquée automatiquement lorsqu'un numéro de TVA UE valide hors d'Allemagne est enregistré"
        }
      ]
    },
    {
      "id": "groups",
      "title": "Groupes et invitations",
      "sections": [
        {
          "title": "Concept de groupe",
          "content": "Les **groupes d'entreprise** vous permettent de gérer plusieurs sites sous une même structure :\n\n- Un **admin de groupe** gère l'ensemble du groupe\n- Les **sites** individuels sont membres du groupe\n- Facturation partagée et administration centralisée\n- Centres de coûts optionnels par site"
        },
        {
          "title": "Invitations",
          "content": "En tant qu'admin de groupe, vous pouvez inviter de nouveaux membres :\n\n1. Accédez au **portail admin de groupe** > « Invitations »\n2. Choisissez « Inviter par e-mail » ou « Inviter par site »\n3. L'invité reçoit un e-mail avec un lien\n4. Après acceptation, le site rejoint le groupe\n\n**Statut :** En attente, Acceptée, Refusée, Expirée, Révoquée"
        },
        {
          "title": "Portail admin de groupe",
          "content": "Le portail admin de groupe propose :\n\n- **Aperçu :** cartes de KPI, statut du groupe, détails du niveau\n- **Membres :** liste de tous les sites avec édition des centres de coûts\n- **Invitations :** envoyer et gérer les invitations\n- **Factures :** factures de groupe au format PDF\n- **Paramètres :** adresse de facturation, numéro de TVA, données SEPA"
        }
      ]
    },
    {
      "id": "sellerPortal",
      "title": "Portail vendeur",
      "sections": [
        {
          "title": "Aperçu",
          "content": "Le **portail vendeur** est l'endroit où vous gérez votre abonnement et tous les détails financiers :\n\n- **Abonnement :** statut, détails du niveau, mise à niveau/rétrogradation, résiliation\n- **Factures :** toutes les factures avec téléchargement PDF\n- **Commissions :** aperçu des revenus de commissions et historique des transactions\n- **Invitations :** accepter ou refuser les invitations de groupe en attente\n- **Paramètres :** adresse de facturation, numéro de TVA, numéro de commande, données SEPA"
        }
      ]
    },
    {
      "id": "externalPortal",
      "title": "Portail fournisseur externe",
      "sections": [
        {
          "title": "Qu'est-ce que le portail externe ?",
          "content": "Le **portail externe** est un espace dédié aux fournisseurs ou transformateurs externes qui souhaitent répondre à des demandes sans compte Surplify complet.\n\n- Ensemble de fonctions limité : consulter les demandes, soumettre des offres, échanger des messages\n- Aucune visibilité sur les matériaux ou groupes internes\n- Idéal pour les partenaires occasionnels"
        },
        {
          "title": "Inscription externe",
          "content": "Les utilisateurs externes peuvent s'inscrire eux-mêmes via une **page d'invitation publique** ou via un lien d'invitation envoyé par un gestionnaire. Après l'inscription, le compte est immédiatement utilisable mais limité aux vues externes."
        },
        {
          "title": "Scénarios et accès externe",
          "content": "Les admins peuvent contrôler si et quand les entreprises externes peuvent accéder aux demandes via des **scénarios**. Le paramètre « External Enabled » dans la configuration du scénario active ou bloque globalement l'accès externe."
        }
      ]
    },
    {
      "id": "commissions",
      "title": "Commissions",
      "sections": [
        {
          "title": "Comment fonctionnent les commissions ?",
          "content": "Pour chaque transaction clôturée avec succès, Surplify prélève une **commission en pourcentage** sur le prix de vente. Le taux dépend du niveau d'abonnement :\n\n- Basic : taux standard\n- Professional : taux réduit\n- Enterprise : négociable individuellement"
        },
        {
          "title": "Tableau de bord des commissions",
          "content": "Sous **« Commissions »**, vous pouvez voir :\n\n- Les modèles de commission actuels (actifs/historiques)\n- L'historique des transactions avec la commission calculée par opération\n- Les totaux mensuels et les chiffres annuels cumulés\n- Le statut d'échéance et les règlements\n\nLes admins peuvent créer, activer et versionner des modèles de commission dans le tableau de bord d'administration."
        }
      ]
    },
    {
      "id": "analytics",
      "title": "Analytique",
      "sections": [
        {
          "title": "Tableau de bord analytique",
          "content": "Le **tableau de bord analytique** offre aux gestionnaires et admins des informations sur :\n\n- Les matériaux les plus consultés (vues, demandes, conversions)\n- Les tendances de revenus et de transactions\n- Les taux de réussite de mise en relation\n- L'activité des utilisateurs par site/groupe\n\nTous les graphiques sont filtrables par période (7/30/90 jours, personnalisé)."
        },
        {
          "title": "Export et reporting",
          "content": "Les résultats analytiques peuvent être exportés au format **CSV ou Excel**. Pour la comptabilité, un **export DATEV** est également disponible dans le tableau de bord d'administration."
        }
      ]
    },
    {
      "id": "importing",
      "title": "Import en masse (CSV/Excel)",
      "sections": [
        {
          "title": "Publier des matériaux par téléversement",
          "content": "Les gestionnaires et admins peuvent importer plusieurs matériaux à la fois :\n\n1. Accédez à **« Import »**\n2. Téléchargez le **modèle Excel** (montre le format de colonnes attendu)\n3. Remplissez le modèle avec vos données\n4. Téléversez le fichier\n5. Vérifiez les résultats de validation (les erreurs sont signalées par ligne)\n6. Choisissez un mode d'import :\n   - **Tout écraser :** remplace les articles existants\n   - **Enrichir uniquement :** ajoute les champs manquants sans écraser les existants\n   - **Sélectionner les champs :** contrôle fin des colonnes mises à jour\n7. Exécutez l'import"
        }
      ]
    },
    {
      "id": "scenarios",
      "title": "Scénarios (admin)",
      "sections": [
        {
          "title": "Modes de scénario",
          "content": "Les admins pilotent le comportement global de la plateforme via des **scénarios** :\n\n- **Équilibré (par défaut) :** règles standard pour la visibilité interne et externe\n- **Conservateur :** visibilité interne plus longue, approbation plus stricte\n- **Aggressive Cash :** externalisation rapide, portée maximale\n- **Mode crise :** accès externe bloqué, opérations internes uniquement\n\nDes paramètres comme la « fenêtre interne » (jours de visibilité interne uniquement) et les « jours d'escalade » (notification automatique pour les demandes anciennes) sont configurables."
        }
      ]
    },
    {
      "id": "bugReports",
      "title": "Signaler des problèmes",
      "sections": [
        {
          "title": "Soumettre un rapport de bug",
          "content": "Si vous repérez un bug :\n\n1. Cliquez sur l'**icône insecte** dans l'en-tête ou ouvrez **« Mes rapports de bug »**\n2. Décrivez le problème le plus précisément possible (URL, étapes, comportement attendu)\n3. Joignez une capture d'écran (facultatif mais recommandé)\n4. Envoyez\n\nLe suivi de statut indique si l'équipe a confirmé (« Ouvert »), travaille (« En cours ») ou a corrigé (« Terminé ») le rapport."
        }
      ]
    },
    {
      "id": "notifications",
      "title": "Notifications",
      "sections": [
        {
          "title": "Configurer les notifications par e-mail",
          "content": "Surplify envoie des e-mails automatiques pour les événements clés :\n\n- Nouvelles demandes et offres\n- **Acceptation d'offre** (acheteur + vendeur reçoivent chacun une confirmation)\n- Correspondances de mise en relation\n- Paiements et rappels\n- Invitations et changements de groupe\n- Escalades (demandes anciennes non traitées)\n\nSous **« Notifications par e-mail »** dans les paramètres, vous pouvez activer ou désactiver chaque type d'événement individuellement."
        }
      ]
    },
    {
      "id": "developerApi",
      "title": "Développeur : API, webhooks, SDK et CLI",
      "sections": [
        {
          "title": "API REST publique",
          "content": "Surplify propose une **API REST** complète (v1 et v2) pour l'intégration dans vos systèmes ERP ou logistiques :\n\n- URL de base : `https://api.surplify.de/api/v1/`\n- Authentification via **clé API** (créée dans le portail développeur)\n- Limites de débit par clé\n- Spécification OpenAPI disponible\n\nLa référence interactive de l'API se trouve sous **« Développeurs » > Documentation de l'API**."
        },
        {
          "title": "Webhooks sortants (signés HMAC)",
          "content": "Recevez des événements en temps réel dans vos systèmes :\n\n1. Créez un endpoint de webhook dans le portail développeur\n2. Abonnez-vous aux événements (par ex. `material.created`, `transaction.completed`, `offer.accepted`)\n3. Chaque payload est **signé en HMAC-SHA256** (en-tête `X-Surplify-Signature`)\n4. Réessais automatiques avec backoff exponentiel en cas d'échec de livraison\n\nValidez les signatures avec l'algorithme documenté dans l'API pour éviter toute falsification."
        },
        {
          "title": "SDK client (Python, Node.js, PHP)",
          "content": "Des SDK générés dynamiquement sont disponibles au téléchargement :\n\n- **SDK Python** (paquet pip)\n- **SDK Node.js** (paquet npm)\n- **SDK PHP** (Composer)\n\nLes SDK sont préconfigurés avec gestion de l'authentification, réessais en cas de limite de débit et modèles typés. Téléchargez-les dans le portail développeur."
        },
        {
          "title": "CLI : npx create-surplify-app",
          "content": "L'**outil officiel de scaffolding** vous permet de démarrer en quelques secondes :\n\n```bash\nnpx create-surplify-app my-integration\ncd my-integration\nnpm install\nnpm run dev\n```\n\nLe projet généré comprend :\n- Un listener de webhook préconfiguré avec validation HMAC\n- Des exemples d'appels API (lister les matériaux, envoyer des demandes)\n- Un modèle `.env` pour la clé API, le secret du webhook et l'URL de base\n- Des tests et le rechargement à chaud\n\nParfait pour créer des intégrations en quelques minutes plutôt qu'en quelques jours."
        }
      ]
    },
    {
      "id": "adminOverview",
      "title": "Aperçu du portail d'administration",
      "sections": [
        {
          "title": "Que peut faire un admin de plateforme ?",
          "content": "Les admins de plateforme ont accès à toutes les couches administratives :\n\n- **Utilisateurs et rôles :** créer, suspendre, modifier les rôles\n- **Abonnements :** ajustements manuels, avoirs commerciaux\n- **Commissions :** créer et versionner des modèles\n- **Mandats SEPA :** inspecter et approuver\n- **Factures :** toutes les factures de la plateforme\n- **Export DATEV :** générer des données comptables\n- **Scénarios :** piloter le comportement global de la plateforme\n- **Journal d'audit :** suivre chaque action liée à la sécurité"
        }
      ]
    }
  ],

  "guide.faq": [
    {
      "category": "Général",
      "questions": [
        { "q": "Qu'est-ce que Surplify ?", "a": "Surplify est une place de marché B2B pour les matériaux d'emballage. Les entreprises peuvent publier des matériaux excédentaires ou rechercher les matériaux dont elles ont besoin." },
        { "q": "Qui peut utiliser Surplify ?", "a": "Surplify s'adresse aux entreprises du secteur de l'emballage : négociants, fabricants et transformateurs. Les petites comme les grandes entreprises profitent de la plateforme." },
        { "q": "L'utilisation est-elle payante ?", "a": "L'inscription est gratuite. Pour les fonctions avancées comme plus d'annonces, la mise en relation ou l'accès API, des niveaux payants sont disponibles." },
        { "q": "Dans quelles langues Surplify est-il disponible ?", "a": "Sept langues : allemand, anglais, espagnol, français, italien, portugais et roumain. Changez via le globe dans l'en-tête." }
      ]
    },
    {
      "category": "Compte et inscription",
      "questions": [
        { "q": "Comment créer un compte ?", "a": "Cliquez sur « S'inscrire », remplissez le formulaire et confirmez votre adresse e-mail. Vous pouvez commencer tout de suite." },
        { "q": "Puis-je changer mon adresse e-mail ?", "a": "Contactez le support pour changer votre e-mail. Pour des raisons de sécurité, cela ne peut pas être fait soi-même." },
        { "q": "Que faire si j'oublie mon mot de passe ?", "a": "Utilisez la fonction « Mot de passe oublié » sur la page de connexion. Vous recevrez un e-mail avec un lien de réinitialisation." },
        { "q": "Quels rôles existent ?", "a": "Négociant, Gestionnaire (admin d'un site), Admin de groupe (gère plusieurs sites), Admin de plateforme et fournisseurs externes. Le rôle détermine quels éléments de menu sont visibles." }
      ]
    },
    {
      "category": "Commerce et matériaux",
      "questions": [
        { "q": "Comment publier un matériau ?", "a": "Cliquez sur « Publier », remplissez le formulaire avec tous les détails pertinents et publiez. Il apparaît instantanément sur le marché." },
        { "q": "Comment fonctionne la mise en relation ?", "a": "Le moteur de mise en relation compare automatiquement les annonces et les recherches selon le type de matériau, la quantité et la localisation. Les deux parties sont notifiées en cas de correspondance." },
        { "q": "Puis-je importer des matériaux en masse ?", "a": "Oui, les gestionnaires et admins peuvent utiliser la fonction d'import pour publier plusieurs matériaux à la fois via téléversement Excel (avec validation et test à blanc)." },
        { "q": "Qu'est-ce que les recherches ?", "a": "Les recherches sont des demandes d'achat actives. Lorsque vous avez besoin d'un matériau précis, créez une recherche. Les vendeurs peuvent répondre avec des offres." },
        { "q": "À quoi sert la liste de suivi ?", "a": "La liste de suivi enregistre les matériaux que vous souhaitez surveiller sans envoyer de demande. Vous pouvez recevoir des alertes facultatives sur les changements de prix ou de statut." }
      ]
    },
    {
      "category": "Messages et offres",
      "questions": [
        { "q": "Comment envoyer une offre contraignante ?", "a": "Ouvrez le fil de messages de la transaction, cliquez sur « Envoyer une offre » et saisissez le prix, la quantité et les conditions. L'offre est mise en évidence dans l'historique du chat." },
        { "q": "Que se passe-t-il quand une offre est acceptée ?", "a": "La transaction est automatiquement mise à jour au prix convenu et les deux parties reçoivent une confirmation par e-mail avec tous les détails (quantité, prix, contact de la contrepartie)." },
        { "q": "Puis-je retirer une offre ?", "a": "Oui, tant que le destinataire n'a pas accepté, vous pouvez cliquer sur « Retirer » dans le fil. Une fois acceptée, l'offre est contraignante." },
        { "q": "Comment fonctionne une contre-offre ?", "a": "Envoyez simplement une nouvelle offre avec des conditions différentes. La précédente reste visible dans l'historique, mais seule la dernière offre acceptée est contraignante." }
      ]
    },
    {
      "category": "Paiements et factures",
      "questions": [
        { "q": "Quels modes de paiement sont pris en charge ?", "a": "Les paiements sont traités via Stripe. La carte de crédit (Visa, Mastercard, Amex) et le prélèvement SEPA sont pris en charge." },
        { "q": "Où trouver mes factures ?", "a": "Dans le portail vendeur sous « Factures » ou dans le portail admin de groupe. Toutes les factures sont téléchargeables au format PDF." },
        { "q": "Qu'est-ce qu'un mandat SEPA ?", "a": "Le mandat SEPA autorise Surplify à prélever des paiements sur votre compte bancaire. Vous pouvez le générer dans les paramètres après avoir enregistré votre IBAN/BIC." },
        { "q": "Comment mon numéro de TVA est-il validé ?", "a": "Surplify valide automatiquement votre numéro de TVA via le système VIES de l'UE. Saisissez-le dans les paramètres de votre portail." },
        { "q": "L'autoliquidation est-elle appliquée ?", "a": "Oui, automatiquement pour un numéro de TVA UE valide hors d'Allemagne. La facture comporte alors la mention correspondante." }
      ]
    },
    {
      "category": "Groupes et sites",
      "questions": [
        { "q": "Qu'est-ce qu'un groupe d'entreprise ?", "a": "Un groupe d'entreprise regroupe plusieurs sites d'une même organisation. Le groupe est géré et facturé de manière centralisée." },
        { "q": "Comment inviter des sites dans mon groupe ?", "a": "Dans le portail admin de groupe sous « Invitations ». Vous pouvez inviter par e-mail ou par recherche de site. Les invitations expirent après un certain temps." },
        { "q": "Un site peut-il appartenir à plusieurs groupes ?", "a": "Non, un site ne peut appartenir qu'à un seul groupe à la fois." }
      ]
    },
    {
      "category": "Portail externe",
      "questions": [
        { "q": "À qui s'adresse le portail externe ?", "a": "Aux partenaires externes occasionnels qui souhaitent répondre à des demandes ou soumettre des offres sans compte complet." },
        { "q": "Comment m'inscrire comme fournisseur externe ?", "a": "Via un lien d'invitation d'un gestionnaire ou la page d'inscription publique pour les fournisseurs externes." },
        { "q": "Puis-je passer d'un compte externe à un compte complet ?", "a": "Oui, la mise à niveau est possible : contactez le support ou le gestionnaire qui vous a invité." }
      ]
    },
    {
      "category": "Administration",
      "questions": [
        { "q": "Que peut faire un admin de plateforme ?", "a": "Gestion des utilisateurs, gestion des abonnements, commissions, mandats SEPA, factures, export DATEV, scénarios, journal d'audit et tous les tableaux de bord analytiques." },
        { "q": "Qu'est-ce que l'export DATEV ?", "a": "L'export DATEV génère des données comptables au format DATEV pour le comptable. Il est disponible dans le tableau de bord d'administration (onglet Exports)." },
        { "q": "Comment gérer les commissions ?", "a": "Dans le tableau de bord des commissions, les admins peuvent configurer des modèles de commission, examiner les transactions et créer des règlements." },
        { "q": "Qu'est-ce que le journal d'audit ?", "a": "Un journal immuable de chaque action liée à la sécurité (connexions, changements de rôle, ajustements de factures), disponible dans le tableau de bord d'administration." }
      ]
    },
    {
      "category": "Développeur et API",
      "questions": [
        { "q": "Comment obtenir une clé API ?", "a": "Dans le portail développeur sous « Clés API », vous pouvez créer une nouvelle clé. Conservez-la en lieu sûr : elle n'est affichée qu'une seule fois." },
        { "q": "Y a-t-il des limites de débit ?", "a": "Oui, par clé API. Les limites exactes sont visibles dans le portail développeur et dépendent de votre niveau. En cas de dépassement, vous recevez un HTTP 429." },
        { "q": "Comment valider les signatures de webhook ?", "a": "Chaque payload est signé en HMAC-SHA256 (en-tête X-Surplify-Signature). Utilisez votre secret de webhook pour valider ; des exemples de code figurent dans la documentation de l'API." },
        { "q": "Qu'est-ce que create-surplify-app ?", "a": "Un outil officiel de scaffolding (npx create-surplify-app my-integration) qui crée un projet d'intégration préconfiguré avec listener de webhook, client API et tests en quelques secondes." },
        { "q": "Quels SDK sont disponibles ?", "a": "Python, Node.js et PHP. Tous sont générés dynamiquement et incluent la gestion de l'authentification, les réessais et des modèles typés." }
      ]
    },
    {
      "category": "Support et langues",
      "questions": [
        { "q": "Comment signaler un bug ?", "a": "Cliquez sur l'icône insecte dans l'en-tête ou ouvrez « Mes rapports de bug ». Décrivez le problème et joignez éventuellement une capture d'écran." },
        { "q": "Comment changer de langue ?", "a": "En haut à droite dans l'en-tête, vous trouverez le globe : cliquez dessus et sélectionnez votre langue. Votre choix est enregistré." },
        { "q": "Comment contacter le support ?", "a": "Pour les questions urgentes, utilisez les coordonnées des mentions légales ou écrivez directement depuis le portail (« Aide > Contacter le support »)." }
      ]
    }
  ]
};

export const guideContentIT = {
  "guide.title": "Aiuto e FAQ",
  "guide.subtitle": "Manuale utente e risposte alle domande frequenti",
  "guide.searchPlaceholder": "Cerca nel manuale e nelle FAQ...",
  "guide.tabGuide": "Manuale utente",
  "guide.tabFaq": "FAQ",
  "guide.noResults": "Nessun risultato per",
  "guide.topic": "argomento",
  "guide.topics": "argomenti",
  "guide.vote.helpful": "Questo articolo è stato utile?",
  "guide.vote.up": "Utile",
  "guide.vote.down": "Non utile",
  "guide.vote.thanksUp": "Grazie per il tuo feedback!",
  "guide.vote.thanksDown": "Grazie — miglioreremo l'articolo.",
  "guide.vote.failed": "Invio del feedback non riuscito",

  "guide.categories": [
    {
      "id": "gettingStarted",
      "title": "Per iniziare",
      "sections": [
        {
          "title": "Registrazione e accesso",
          "content": "Per usare Surplify, registrati con la tua e-mail aziendale. Dopo la registrazione accedi alla tua dashboard personale.\n\n**Come registrarsi:**\n1. Clicca su \"Registrati\" in alto a destra\n2. Inserisci nome, e-mail e password\n3. Scegli il tuo ruolo (Commerciante, Manager, ecc.)\n4. Dopo la registrazione vieni connesso automaticamente\n\n**Accesso:** usa l'e-mail registrata e la password nella pagina di accesso."
        },
        {
          "title": "Panoramica della navigazione",
          "content": "Il menu principale si trova in alto a destra, sotto il tuo nome utente. È organizzato in:\n\n- **Mercato** — i tuoi articoli, lista preferiti, ricerche, materiali standard\n- **Commercio e comunicazione** — richieste, transazioni, abbinamento, messaggi\n- **Amministrazione** — dashboard, analisi, importazione (solo manager/admin)\n- **Portali** — portale venditore, portale admin di gruppo, portale esterno\n- **Sviluppatori** — API, webhook, SDK, CLI\n- **Impostazioni** — notifiche, aiuto, lingua"
        },
        {
          "title": "Cambiare la lingua",
          "content": "Surplify è disponibile in più lingue (tedesco, inglese, spagnolo, francese, italiano, portoghese, rumeno).\n\n**Per cambiare lingua:**\n1. Clicca sul selettore di lingua (icona globo) nell'intestazione\n2. Seleziona la lingua desiderata\n3. L'intera interfaccia viene tradotta all'istante\n\nLa tua scelta viene salvata nel browser e ripristinata alla visita successiva."
        }
      ]
    },
    {
      "id": "marketplace",
      "title": "Mercato",
      "sections": [
        {
          "title": "Pubblicare un materiale",
          "content": "Per pubblicare un materiale sul mercato:\n\n1. Clicca su **\"Pubblica\"** nella navigazione in alto\n2. Compila il modulo: nome, categoria, descrizione, quantità, prezzo\n3. Aggiungi immagini e specifiche opzionali\n4. Clicca su \"Pubblica\"\n\n**Consigli:** descrizioni dettagliate e immagini aumentano la visibilità."
        },
        {
          "title": "Cercare e trovare materiali",
          "content": "Usa la barra di ricerca centrale per trovare materiali. Puoi cercare per:\n\n- **Nome del materiale** o **categoria**\n- **Filtri:** tipo, prezzo, quantità, posizione\n- Ordinare i risultati per pertinenza, prezzo o data\n\nLa paginazione e l'ordinamento (data, prezzo, quantità) sono disponibili per grandi insiemi di risultati."
        },
        {
          "title": "Lista preferiti",
          "content": "La **lista preferiti** ti permette di tenere d'occhio i materiali interessanti senza inviare subito una richiesta.\n\n**Come funziona:**\n1. Clicca sull'**icona a cuore** su una scheda o pagina di dettaglio di un materiale\n2. Il materiale viene aggiunto alla tua lista preferiti personale\n3. Apri \"Lista preferiti\" nel menu per vedere tutti gli articoli salvati\n\n**Notifiche:** puoi scegliere di ricevere avvisi quando il prezzo scende o cambia lo stato di un materiale seguito."
        },
        {
          "title": "Creare ricerche (annunci di domanda)",
          "content": "In **\"Ricerche\"** puoi pubblicare richieste di acquisto attive:\n\n1. Vai a Ricerche\n2. Crea una nuova ricerca con tipo, quantità e budget\n3. I venditori possono vedere le tue ricerche e rispondere\n\nLe ricerche attive vengono considerate anche dal motore di abbinamento."
        },
        {
          "title": "Materiali standard",
          "content": "L'elenco dei materiali standard contiene tipi di materiale predefiniti per la tua organizzazione. Servono come modello nella creazione degli annunci affinché l'inventario resti coerente. I manager possono gestire l'elenco in modo centralizzato."
        }
      ]
    },
    {
      "id": "trading",
      "title": "Commercio e transazioni",
      "sections": [
        {
          "title": "Inviare e gestire le richieste",
          "content": "Se sei interessato a un materiale:\n\n1. Apri la pagina di dettaglio del materiale\n2. Clicca su **\"Invia richiesta\"**\n3. Inserisci la quantità desiderata e un commento opzionale\n4. La richiesta viene inviata al venditore\n\nLe **richieste in arrivo** si trovano in \"Richieste\" nel tuo menu con filtri di stato (Aperta, Accettata, Rifiutata)."
        },
        {
          "title": "Transazioni",
          "content": "In **\"Transazioni\"** vedi tutte le operazioni in corso e concluse:\n\n- **Stato:** Aperta, In trattativa, Completata, Annullata\n- **Dettagli:** quantità, prezzo, info acquirente/venditore, allegati\n- Ogni transazione può essere commentata e genera automaticamente un thread di messaggi"
        },
        {
          "title": "Abbinamento intelligente",
          "content": "Il **motore di abbinamento** confronta automaticamente i tuoi annunci con le ricerche e viceversa:\n\n- Gli abbinamenti vengono calcolati in tempo reale (tipo di materiale, quantità, regione, fascia di prezzo)\n- Ricevi avvisi via e-mail sui nuovi abbinamenti\n- Clicca su un abbinamento per avviare direttamente una richiesta o un messaggio\n\nLa dashboard di abbinamento (manager/admin) mostra il tasso di successo e i migliori abbinamenti."
        }
      ]
    },
    {
      "id": "messaging",
      "title": "Messaggi e offerte",
      "sections": [
        {
          "title": "Thread di messaggi",
          "content": "Il **sistema di messaggistica** integrato consente la comunicazione diretta 1:1 tra acquirente e venditore:\n\n- Ogni transazione genera automaticamente un thread\n- I messaggi non letti vengono mostrati come badge nel menu\n- Puoi allegare file (specifiche, foto) ai messaggi"
        },
        {
          "title": "Inviare offerte",
          "content": "In qualsiasi thread puoi inviare un'**offerta vincolante**:\n\n1. Apri il thread\n2. Clicca su **\"Invia offerta\"**\n3. Inserisci prezzo, quantità e condizioni opzionali (termine di ritiro, condizioni di pagamento)\n4. L'offerta appare come messaggio evidenziato con i pulsanti \"Accetta\" / \"Rifiuta\"\n\nLe offerte hanno uno stato chiaro: **Aperta**, **Accettata**, **Rifiutata** o **Ritirata**."
        },
        {
          "title": "Accettare o rifiutare un'offerta",
          "content": "Come destinatario di un'offerta:\n\n- **Accetta:** clicca su \"Accetta\". La transazione viene aggiornata automaticamente al prezzo concordato ed entrambe le parti ricevono una **conferma automatica via e-mail** con tutti i dettagli (quantità, prezzo, controparte, contatto).\n- **Rifiuta:** clicca su \"Rifiuta\". L'offerta viene contrassegnata come rifiutata. Puoi inviare una controproposta.\n\nTutte le azioni vengono registrate nella cronologia del thread."
        },
        {
          "title": "Controproposte e trattativa",
          "content": "Puoi inviare una **controproposta** in qualsiasi momento semplicemente inviando una nuova offerta con condizioni diverse. L'offerta precedente resta visibile nella cronologia, ma solo l'ultima offerta accettata è vincolante."
        }
      ]
    },
    {
      "id": "subscriptions",
      "title": "Abbonamenti e pagamenti",
      "sections": [
        {
          "title": "Piani e livelli",
          "content": "Surplify offre diversi livelli di abbonamento:\n\n- **Basic** — funzioni essenziali per iniziare\n- **Professional** — funzioni avanzate, più annunci\n- **Enterprise** — set completo di funzioni, incl. funzionalità di gruppo e accesso API\n\nGli upgrade e i downgrade sono disponibili in qualsiasi momento dal **Portale venditore** con fatturazione pro-rata automatica."
        },
        {
          "title": "Pagamenti tramite Stripe",
          "content": "Tutti i pagamenti avvengono in modo sicuro tramite **Stripe**:\n\n1. Scegli il livello desiderato\n2. Vieni reindirizzato a Stripe Checkout\n3. Dopo il pagamento riuscito il tuo abbonamento viene attivato all'istante\n\nMetodi supportati: carta di credito (Visa, Mastercard, Amex) e addebito SEPA. Le **fatture** sono disponibili nel Portale venditore sotto \"Fatture\"."
        },
        {
          "title": "Mandato di addebito SEPA",
          "content": "Per i pagamenti SEPA puoi salvare il tuo **IBAN e BIC**:\n\n1. Vai al tuo Portale (venditore o admin di gruppo)\n2. In \"Impostazioni\" trovi i dati di pagamento\n3. Inserisci IBAN e BIC\n4. Scarica il tuo mandato SEPA in PDF\n\nIl mandato viene generato automaticamente dai dati attuali della tua azienda ed è pienamente conforme SEPA."
        },
        {
          "title": "Fatture e partita IVA",
          "content": "- Le **fatture** vengono generate automaticamente in PDF e possono essere scaricate in qualsiasi momento\n- La **validazione della partita IVA** avviene automaticamente tramite il sistema VIES dell'UE\n- L'**indirizzo di fatturazione** può essere modificato nelle impostazioni del tuo portale\n- L'**inversione contabile** viene applicata automaticamente quando è registrata una partita IVA UE valida al di fuori della Germania"
        }
      ]
    },
    {
      "id": "groups",
      "title": "Gruppi e inviti",
      "sections": [
        {
          "title": "Concetto di gruppo",
          "content": "I **gruppi aziendali** ti permettono di gestire più sedi sotto un'unica struttura:\n\n- Un **admin di gruppo** gestisce l'intero gruppo\n- Le singole **sedi** sono membri del gruppo\n- Fatturazione condivisa e amministrazione centralizzata\n- Centri di costo opzionali per sede"
        },
        {
          "title": "Inviti",
          "content": "Come admin di gruppo puoi invitare nuovi membri:\n\n1. Vai al **Portale admin di gruppo** > \"Inviti\"\n2. Scegli \"Invita via e-mail\" o \"Invita per sede\"\n3. L'invitato riceve un'e-mail con un link\n4. Dopo l'accettazione la sede si unisce al gruppo\n\n**Stato:** In attesa, Accettato, Rifiutato, Scaduto, Revocato"
        },
        {
          "title": "Portale admin di gruppo",
          "content": "Il Portale admin di gruppo offre:\n\n- **Panoramica:** schede KPI, stato del gruppo, dettagli del livello\n- **Membri:** elenco di tutte le sedi con modifica dei centri di costo\n- **Inviti:** inviare e gestire gli inviti\n- **Fatture:** fatture di gruppo in PDF\n- **Impostazioni:** indirizzo di fatturazione, partita IVA, dati SEPA"
        }
      ]
    },
    {
      "id": "sellerPortal",
      "title": "Portale venditore",
      "sections": [
        {
          "title": "Panoramica",
          "content": "Nel **Portale venditore** gestisci il tuo abbonamento e tutti i dettagli finanziari:\n\n- **Abbonamento:** stato, dettagli del livello, upgrade/downgrade, disdetta\n- **Fatture:** tutte le fatture con download PDF\n- **Commissioni:** panoramica dei ricavi da commissioni e cronologia delle transazioni\n- **Inviti:** accettare o rifiutare gli inviti di gruppo in attesa\n- **Impostazioni:** indirizzo di fatturazione, partita IVA, numero d'ordine, dati SEPA"
        }
      ]
    },
    {
      "id": "externalPortal",
      "title": "Portale fornitori esterni",
      "sections": [
        {
          "title": "Cos'è il portale esterno?",
          "content": "Il **portale esterno** è un'area dedicata a fornitori o trasformatori esterni che vogliono rispondere alle richieste senza un account Surplify completo.\n\n- Set di funzioni limitato: vedere le richieste, inviare offerte, scambiare messaggi\n- Nessuna visibilità su materiali o gruppi interni\n- Ideale per partner occasionali"
        },
        {
          "title": "Registrazione esterna",
          "content": "Gli utenti esterni possono registrarsi tramite una **pagina di invito pubblica** o tramite un link di invito inviato da un manager. Dopo la registrazione l'account è immediatamente utilizzabile ma limitato alle viste esterne."
        },
        {
          "title": "Scenari e accesso esterno",
          "content": "Gli admin possono controllare se e quando le aziende esterne possono accedere alle richieste tramite **scenari**. L'impostazione \"External Enabled\" nella configurazione dello scenario abilita o blocca l'accesso esterno a livello globale."
        }
      ]
    },
    {
      "id": "commissions",
      "title": "Commissioni",
      "sections": [
        {
          "title": "Come funzionano le commissioni?",
          "content": "Per ogni transazione conclusa con successo, Surplify applica una **commissione percentuale** sul prezzo di vendita. La tariffa dipende dal livello di abbonamento:\n\n- Basic: tariffa standard\n- Professional: tariffa ridotta\n- Enterprise: negoziabile individualmente"
        },
        {
          "title": "Dashboard delle commissioni",
          "content": "In **\"Commissioni\"** puoi vedere:\n\n- I modelli di commissione attuali (attivi/storici)\n- La cronologia delle transazioni con la commissione calcolata per operazione\n- I totali mensili e le cifre annuali cumulate\n- Lo stato di scadenza e i conguagli\n\nGli admin possono creare, attivare e versionare i modelli di commissione nella dashboard di amministrazione."
        }
      ]
    },
    {
      "id": "analytics",
      "title": "Analisi",
      "sections": [
        {
          "title": "Dashboard di analisi",
          "content": "La **dashboard di analisi** offre a manager e admin informazioni su:\n\n- Materiali più visti (visualizzazioni, richieste, conversioni)\n- Andamento di ricavi e transazioni\n- Tassi di successo dell'abbinamento\n- Attività degli utenti per sede/gruppo\n\nTutti i grafici sono filtrabili per periodo (7/30/90 giorni, personalizzato)."
        },
        {
          "title": "Esportazione e reportistica",
          "content": "I risultati delle analisi possono essere esportati in **CSV o Excel**. Per la contabilità è disponibile anche l'**esportazione DATEV** nella dashboard di amministrazione."
        }
      ]
    },
    {
      "id": "importing",
      "title": "Importazione in blocco (CSV/Excel)",
      "sections": [
        {
          "title": "Pubblicare materiali tramite caricamento",
          "content": "Manager e admin possono importare più materiali contemporaneamente:\n\n1. Vai a **\"Importa\"**\n2. Scarica il **modello Excel** (mostra il formato di colonne previsto)\n3. Compila il modello con i tuoi dati\n4. Carica il file\n5. Controlla i risultati di validazione (gli errori sono evidenziati per riga)\n6. Scegli una modalità di importazione:\n   - **Sovrascrivi tutto:** sostituisce gli articoli esistenti\n   - **Solo arricchisci:** aggiunge i campi mancanti senza sovrascrivere quelli esistenti\n   - **Seleziona campi:** controllo dettagliato sulle colonne aggiornate\n7. Esegui l'importazione"
        }
      ]
    },
    {
      "id": "scenarios",
      "title": "Scenari (admin)",
      "sections": [
        {
          "title": "Modalità di scenario",
          "content": "Gli admin guidano il comportamento globale della piattaforma tramite **scenari**:\n\n- **Bilanciato (predefinito):** regole standard per la visibilità interna ed esterna\n- **Conservativo:** visibilità interna più lunga, approvazione più rigorosa\n- **Aggressive Cash:** esternalizzazione rapida, massima portata\n- **Modalità crisi:** accesso esterno bloccato, solo operazioni interne\n\nParametri come la \"finestra interna\" (giorni di visibilità solo interna) e i \"giorni di escalation\" (notifica automatica per le richieste datate) sono configurabili."
        }
      ]
    },
    {
      "id": "bugReports",
      "title": "Segnalare problemi",
      "sections": [
        {
          "title": "Inviare una segnalazione di bug",
          "content": "Se noti un bug:\n\n1. Clicca sull'**icona dell'insetto** nell'intestazione o apri **\"Le mie segnalazioni\"**\n2. Descrivi il problema il più precisamente possibile (URL, passaggi, comportamento atteso)\n3. Allega uno screenshot (facoltativo ma consigliato)\n4. Invia\n\nIl tracciamento dello stato indica se il team ha confermato (\"Aperto\"), sta lavorando (\"In corso\") o ha risolto (\"Fatto\") la segnalazione."
        }
      ]
    },
    {
      "id": "notifications",
      "title": "Notifiche",
      "sections": [
        {
          "title": "Configurare le notifiche e-mail",
          "content": "Surplify invia e-mail automatiche per gli eventi chiave:\n\n- Nuove richieste e offerte\n- **Accettazione dell'offerta** (acquirente + venditore ricevono ciascuno una conferma)\n- Abbinamenti riusciti\n- Pagamenti e promemoria\n- Inviti e modifiche di gruppo\n- Escalation (richieste datate non gestite)\n\nIn **\"Notifiche e-mail\"** nelle impostazioni puoi attivare o disattivare ogni tipo di evento singolarmente."
        }
      ]
    },
    {
      "id": "developerApi",
      "title": "Sviluppatore: API, webhook, SDK e CLI",
      "sections": [
        {
          "title": "API REST pubblica",
          "content": "Surplify offre un'**API REST** completa (v1 e v2) per l'integrazione nei tuoi sistemi ERP o logistici:\n\n- URL di base: `https://api.surplify.de/api/v1/`\n- Autenticazione tramite **chiave API** (creala nel portale sviluppatori)\n- Limiti di velocità per chiave\n- Specifica OpenAPI disponibile\n\nIl riferimento interattivo dell'API si trova in **\"Sviluppatori\" > Documentazione API**."
        },
        {
          "title": "Webhook in uscita (firmati HMAC)",
          "content": "Ricevi eventi in tempo reale nei tuoi sistemi:\n\n1. Crea un endpoint webhook nel portale sviluppatori\n2. Iscriviti agli eventi (es. `material.created`, `transaction.completed`, `offer.accepted`)\n3. Ogni payload è **firmato in HMAC-SHA256** (header `X-Surplify-Signature`)\n4. Tentativi automatici con backoff esponenziale in caso di errori di consegna\n\nConvalida le firme con l'algoritmo documentato nell'API per evitare manomissioni."
        },
        {
          "title": "SDK client (Python, Node.js, PHP)",
          "content": "Sono disponibili per il download SDK generati dinamicamente:\n\n- **SDK Python** (pacchetto pip)\n- **SDK Node.js** (pacchetto npm)\n- **SDK PHP** (Composer)\n\nGli SDK sono preconfigurati con gestione dell'autenticazione, tentativi per limite di velocità e modelli tipizzati. Scaricali nel portale sviluppatori."
        },
        {
          "title": "CLI: npx create-surplify-app",
          "content": "Lo **strumento ufficiale di scaffolding** ti permette di iniziare in pochi secondi:\n\n```bash\nnpx create-surplify-app my-integration\ncd my-integration\nnpm install\nnpm run dev\n```\n\nIl progetto generato include:\n- Un listener webhook preconfigurato con validazione HMAC\n- Esempi di chiamate API (elencare materiali, inviare richieste)\n- Un modello `.env` per chiave API, segreto del webhook e URL di base\n- Test e ricaricamento a caldo\n\nPerfetto per creare integrazioni in minuti anziché giorni."
        }
      ]
    },
    {
      "id": "adminOverview",
      "title": "Panoramica del portale di amministrazione",
      "sections": [
        {
          "title": "Cosa può fare un admin di piattaforma?",
          "content": "Gli admin di piattaforma hanno accesso a tutti i livelli amministrativi:\n\n- **Utenti e ruoli:** creare, sospendere, modificare ruoli\n- **Abbonamenti:** rettifiche manuali, accrediti di cortesia\n- **Commissioni:** creare e versionare modelli\n- **Mandati SEPA:** ispezionare e approvare\n- **Fatture:** tutte le fatture della piattaforma\n- **Esportazione DATEV:** generare dati contabili\n- **Scenari:** guidare il comportamento globale della piattaforma\n- **Registro di audit:** tracciare ogni azione rilevante per la sicurezza"
        }
      ]
    }
  ],

  "guide.faq": [
    {
      "category": "Generale",
      "questions": [
        { "q": "Cos'è Surplify?", "a": "Surplify è un marketplace B2B per materiali da imballaggio. Le aziende possono pubblicare materiali in eccedenza o cercare i materiali di cui hanno bisogno." },
        { "q": "Chi può usare Surplify?", "a": "Surplify è rivolto alle aziende del settore dell'imballaggio: commercianti, produttori e trasformatori. Sia le piccole che le grandi aziende traggono vantaggio dalla piattaforma." },
        { "q": "L'utilizzo è a pagamento?", "a": "La registrazione è gratuita. Per le funzioni avanzate come più annunci, abbinamento o accesso API sono disponibili livelli a pagamento." },
        { "q": "In quali lingue è disponibile Surplify?", "a": "Sette lingue: tedesco, inglese, spagnolo, francese, italiano, portoghese e rumeno. Cambia tramite il globo nell'intestazione." }
      ]
    },
    {
      "category": "Account e registrazione",
      "questions": [
        { "q": "Come creo un account?", "a": "Clicca su \"Registrati\", compila il modulo e conferma il tuo indirizzo e-mail. Puoi iniziare subito." },
        { "q": "Posso cambiare il mio indirizzo e-mail?", "a": "Contatta l'assistenza per cambiare la tua e-mail. Per motivi di sicurezza non è possibile farlo autonomamente." },
        { "q": "Cosa succede se dimentico la password?", "a": "Usa la funzione \"Password dimenticata\" nella pagina di accesso. Riceverai un'e-mail con un link per il reset." },
        { "q": "Quali ruoli esistono?", "a": "Commerciante, Manager (admin di una sede), Admin di gruppo (gestisce più sedi), Admin di piattaforma e fornitori esterni. Il ruolo determina quali voci di menu sono visibili." }
      ]
    },
    {
      "category": "Commercio e materiali",
      "questions": [
        { "q": "Come pubblico un materiale?", "a": "Clicca su \"Pubblica\", compila il modulo con tutti i dettagli pertinenti e pubblica. Appare subito sul mercato." },
        { "q": "Come funziona l'abbinamento?", "a": "Il motore di abbinamento confronta automaticamente annunci e ricerche in base a tipo di materiale, quantità e posizione. Entrambe le parti vengono notificate in caso di corrispondenza." },
        { "q": "Posso importare materiali in blocco?", "a": "Sì, manager e admin possono usare la funzione di importazione per pubblicare più materiali contemporaneamente tramite caricamento Excel (con validazione e prova a vuoto)." },
        { "q": "Cosa sono le ricerche?", "a": "Le ricerche sono richieste di acquisto attive. Quando hai bisogno di un materiale specifico, crea una ricerca. I venditori possono rispondere con offerte." },
        { "q": "A cosa serve la lista preferiti?", "a": "La lista preferiti salva i materiali che vuoi tenere d'occhio senza inviare una richiesta. Puoi ricevere avvisi opzionali su variazioni di prezzo o stato." }
      ]
    },
    {
      "category": "Messaggi e offerte",
      "questions": [
        { "q": "Come invio un'offerta vincolante?", "a": "Apri il thread di messaggi della transazione, clicca su \"Invia offerta\" e inserisci prezzo, quantità e condizioni. L'offerta è evidenziata nella cronologia della chat." },
        { "q": "Cosa succede quando un'offerta viene accettata?", "a": "La transazione viene aggiornata automaticamente al prezzo concordato ed entrambe le parti ricevono una conferma via e-mail con tutti i dettagli (quantità, prezzo, contatto della controparte)." },
        { "q": "Posso ritirare un'offerta?", "a": "Sì, finché il destinatario non ha accettato, puoi cliccare su \"Ritira\" nel thread. Una volta accettata, l'offerta è vincolante." },
        { "q": "Come funziona una controproposta?", "a": "Invia semplicemente una nuova offerta con condizioni diverse. La precedente resta visibile nella cronologia, ma solo l'ultima offerta accettata è vincolante." }
      ]
    },
    {
      "category": "Pagamenti e fatture",
      "questions": [
        { "q": "Quali metodi di pagamento sono supportati?", "a": "I pagamenti avvengono tramite Stripe. Sono supportati carta di credito (Visa, Mastercard, Amex) e addebito SEPA." },
        { "q": "Dove trovo le mie fatture?", "a": "Nel Portale venditore sotto \"Fatture\" o nel Portale admin di gruppo. Tutte le fatture possono essere scaricate in PDF." },
        { "q": "Cos'è un mandato SEPA?", "a": "Il mandato SEPA autorizza Surplify ad addebitare i pagamenti sul tuo conto bancario. Puoi generarlo nelle impostazioni dopo aver salvato il tuo IBAN/BIC." },
        { "q": "Come viene convalidata la mia partita IVA?", "a": "Surplify convalida automaticamente la tua partita IVA tramite il sistema VIES dell'UE. Inseriscila nelle impostazioni del tuo portale." },
        { "q": "Viene applicata l'inversione contabile?", "a": "Sì, automaticamente per una partita IVA UE valida al di fuori della Germania. La fattura include quindi la relativa dicitura." }
      ]
    },
    {
      "category": "Gruppi e sedi",
      "questions": [
        { "q": "Cos'è un gruppo aziendale?", "a": "Un gruppo aziendale raggruppa più sedi di una stessa organizzazione. Il gruppo viene gestito e fatturato in modo centralizzato." },
        { "q": "Come invito le sedi nel mio gruppo?", "a": "Nel Portale admin di gruppo sotto \"Inviti\". Puoi invitare via e-mail o tramite ricerca di sedi. Gli inviti scadono dopo un certo tempo." },
        { "q": "Una sede può appartenere a più gruppi?", "a": "No, una sede può appartenere a un solo gruppo alla volta." }
      ]
    },
    {
      "category": "Portale esterno",
      "questions": [
        { "q": "A chi è destinato il portale esterno?", "a": "A partner esterni occasionali che vogliono rispondere alle richieste o inviare offerte senza un account completo." },
        { "q": "Come mi registro come fornitore esterno?", "a": "Tramite un link di invito di un manager o la pagina di registrazione pubblica per i fornitori esterni." },
        { "q": "Posso passare da un account esterno a uno completo?", "a": "Sì, l'upgrade è possibile: contatta l'assistenza o il manager che ti ha invitato." }
      ]
    },
    {
      "category": "Amministrazione",
      "questions": [
        { "q": "Cosa può fare un admin di piattaforma?", "a": "Gestione utenti, gestione abbonamenti, commissioni, mandati SEPA, fatture, esportazione DATEV, scenari, registro di audit e tutte le dashboard analitiche." },
        { "q": "Cos'è l'esportazione DATEV?", "a": "L'esportazione DATEV genera dati contabili in formato DATEV per il commercialista. È disponibile nella dashboard di amministrazione (scheda Esportazioni)." },
        { "q": "Come gestisco le commissioni?", "a": "Nella dashboard delle commissioni gli admin possono configurare modelli di commissione, esaminare le transazioni e creare conguagli." },
        { "q": "Cos'è il registro di audit?", "a": "Un registro immutabile di ogni azione rilevante per la sicurezza (accessi, modifiche di ruolo, rettifiche di fatture), disponibile nella dashboard di amministrazione." }
      ]
    },
    {
      "category": "Sviluppatore e API",
      "questions": [
        { "q": "Come ottengo una chiave API?", "a": "Nel portale sviluppatori sotto \"Chiavi API\" puoi creare una nuova chiave. Conservala in modo sicuro: viene mostrata una sola volta." },
        { "q": "Ci sono limiti di velocità?", "a": "Sì, per chiave API. I limiti esatti sono visibili nel portale sviluppatori e dipendono dal tuo livello. Al superamento ricevi un HTTP 429." },
        { "q": "Come convalido le firme dei webhook?", "a": "Ogni payload è firmato con HMAC-SHA256 (header X-Surplify-Signature). Usa il tuo segreto del webhook per convalidare; esempi di codice sono nella documentazione API." },
        { "q": "Cos'è create-surplify-app?", "a": "Uno strumento ufficiale di scaffolding (npx create-surplify-app my-integration) che crea un progetto di integrazione preconfigurato con listener webhook, client API e test in pochi secondi." },
        { "q": "Quali SDK sono disponibili?", "a": "Python, Node.js e PHP. Tutti generati dinamicamente e includono gestione dell'autenticazione, tentativi e modelli tipizzati." }
      ]
    },
    {
      "category": "Assistenza e lingue",
      "questions": [
        { "q": "Come segnalo un bug?", "a": "Clicca sull'icona dell'insetto nell'intestazione o apri \"Le mie segnalazioni\". Descrivi il problema e allega facoltativamente uno screenshot." },
        { "q": "Come cambio la lingua?", "a": "In alto a destra nell'intestazione trovi il globo: cliccaci sopra e seleziona la tua lingua. La tua scelta viene salvata." },
        { "q": "Come contatto l'assistenza?", "a": "Per questioni urgenti usa i recapiti nelle note legali o scrivi direttamente dal portale (\"Aiuto > Contatta l'assistenza\")." }
      ]
    }
  ]
};

export const guideContentPT = {
  "guide.title": "Ajuda e FAQ",
  "guide.subtitle": "Manual do utilizador e respostas às perguntas frequentes",
  "guide.searchPlaceholder": "Pesquisar no manual e nas FAQ...",
  "guide.tabGuide": "Manual do utilizador",
  "guide.tabFaq": "FAQ",
  "guide.noResults": "Sem resultados para",
  "guide.topic": "tópico",
  "guide.topics": "tópicos",
  "guide.vote.helpful": "Este artigo foi útil?",
  "guide.vote.up": "Útil",
  "guide.vote.down": "Não útil",
  "guide.vote.thanksUp": "Obrigado pelo seu feedback!",
  "guide.vote.thanksDown": "Obrigado — vamos melhorar o artigo.",
  "guide.vote.failed": "Falha ao enviar o feedback",

  "guide.categories": [
    {
      "id": "gettingStarted",
      "title": "Primeiros passos",
      "sections": [
        {
          "title": "Registo e início de sessão",
          "content": "Para usar a Surplify, registe-se com o seu e-mail profissional. Após o registo, terá acesso ao seu painel pessoal.\n\n**Como registar-se:**\n1. Clique em \"Registar\" no canto superior direito\n2. Introduza o seu nome, e-mail e palavra-passe\n3. Escolha a sua função (Comerciante, Gestor, etc.)\n4. Após o registo, fica automaticamente com sessão iniciada\n\n**Início de sessão:** use o seu e-mail registado e a palavra-passe na página de início de sessão."
        },
        {
          "title": "Visão geral da navegação",
          "content": "O menu principal está no canto superior direito, por baixo do seu nome de utilizador. Está organizado em:\n\n- **Mercado** — os seus artigos, lista de favoritos, procuras, materiais padrão\n- **Comércio e comunicação** — pedidos, transações, correspondência, mensagens\n- **Administração** — painel, análise, importação (apenas gestores/admins)\n- **Portais** — portal do vendedor, portal de admin de grupo, portal externo\n- **Programadores** — API, webhooks, SDK, CLI\n- **Definições** — notificações, ajuda, idioma"
        },
        {
          "title": "Alterar o idioma",
          "content": "A Surplify está disponível em vários idiomas (alemão, inglês, espanhol, francês, italiano, português, romeno).\n\n**Para mudar de idioma:**\n1. Clique no seletor de idioma (ícone de globo) no cabeçalho\n2. Selecione o idioma pretendido\n3. Toda a interface é traduzida instantaneamente\n\nA sua escolha é guardada no navegador e restaurada na próxima visita."
        }
      ]
    },
    {
      "id": "marketplace",
      "title": "Mercado",
      "sections": [
        {
          "title": "Publicar um material",
          "content": "Para publicar um material no mercado:\n\n1. Clique em **\"Publicar\"** na navegação superior\n2. Preencha o formulário: nome, categoria, descrição, quantidade, preço\n3. Adicione imagens e especificações opcionais\n4. Clique em \"Publicar\"\n\n**Dicas:** descrições detalhadas e imagens aumentam a visibilidade."
        },
        {
          "title": "Procurar e encontrar materiais",
          "content": "Use a barra de pesquisa central para encontrar materiais. Pode pesquisar por:\n\n- **Nome do material** ou **categoria**\n- **Filtros:** tipo, preço, quantidade, localização\n- Ordenar resultados por relevância, preço ou data\n\nA paginação e a ordenação (data, preço, quantidade) estão disponíveis para grandes conjuntos de resultados."
        },
        {
          "title": "Lista de favoritos",
          "content": "A **lista de favoritos** permite-lhe acompanhar materiais interessantes sem fazer um pedido imediato.\n\n**Como funciona:**\n1. Clique no **ícone de coração** num cartão ou página de detalhe de um material\n2. O material é adicionado à sua lista de favoritos pessoal\n3. Abra \"Lista de favoritos\" no menu para ver todos os artigos guardados\n\n**Notificações:** pode optar por receber alertas quando o preço desce ou o estado de um material acompanhado muda."
        },
        {
          "title": "Criar procuras (anúncios de procura)",
          "content": "Em **\"Procuras\"** pode publicar pedidos de compra ativos:\n\n1. Vá a Procuras\n2. Crie uma nova procura com tipo, quantidade e orçamento\n3. Os vendedores podem ver as suas procuras e responder\n\nAs procuras ativas também são consideradas pelo motor de correspondência."
        },
        {
          "title": "Materiais padrão",
          "content": "A lista de materiais padrão contém tipos de material predefinidos para a sua organização. Servem de modelo na criação de anúncios para que o inventário se mantenha consistente. Os gestores podem manter a lista de forma centralizada."
        }
      ]
    },
    {
      "id": "trading",
      "title": "Comércio e transações",
      "sections": [
        {
          "title": "Enviar e gerir pedidos",
          "content": "Se estiver interessado num material:\n\n1. Abra a página de detalhe do material\n2. Clique em **\"Enviar pedido\"**\n3. Introduza a quantidade pretendida e um comentário opcional\n4. O pedido é enviado ao vendedor\n\nOs **pedidos recebidos** estão em \"Pedidos\" no seu menu, com filtros de estado (Aberto, Aceite, Recusado)."
        },
        {
          "title": "Transações",
          "content": "Em **\"Transações\"** vê todas as operações em curso e concluídas:\n\n- **Estado:** Aberta, Em negociação, Concluída, Cancelada\n- **Detalhes:** quantidade, preço, dados do comprador/vendedor, anexos\n- Cada transação pode ser comentada e gera automaticamente um tópico de mensagens"
        },
        {
          "title": "Correspondência inteligente",
          "content": "O **motor de correspondência** compara automaticamente os seus anúncios com as procuras e vice-versa:\n\n- As correspondências são calculadas em tempo real (tipo de material, quantidade, região, faixa de preço)\n- Recebe alertas por e-mail sobre novas correspondências\n- Clique numa correspondência para iniciar diretamente um pedido ou mensagem\n\nO painel de correspondência (gestor/admin) mostra a taxa de acerto e as melhores correspondências."
        }
      ]
    },
    {
      "id": "messaging",
      "title": "Mensagens e ofertas",
      "sections": [
        {
          "title": "Tópicos de mensagens",
          "content": "O **sistema de mensagens** integrado permite a comunicação direta 1:1 entre comprador e vendedor:\n\n- Cada transação gera automaticamente um tópico\n- As mensagens não lidas são mostradas como um selo no menu\n- Pode anexar ficheiros (especificações, fotos) às mensagens"
        },
        {
          "title": "Enviar ofertas",
          "content": "Em qualquer tópico pode enviar uma **oferta vinculativa**:\n\n1. Abra o tópico\n2. Clique em **\"Enviar oferta\"**\n3. Introduza preço, quantidade e condições opcionais (prazo de recolha, condições de pagamento)\n4. A oferta aparece como uma mensagem destacada com os botões \"Aceitar\" / \"Recusar\"\n\nAs ofertas têm um estado claro: **Aberta**, **Aceite**, **Recusada** ou **Retirada**."
        },
        {
          "title": "Aceitar ou recusar uma oferta",
          "content": "Como destinatário de uma oferta:\n\n- **Aceitar:** clique em \"Aceitar\". A transação é atualizada automaticamente para o preço acordado e ambas as partes recebem uma **confirmação automática por e-mail** com todos os detalhes (quantidade, preço, contraparte, contacto).\n- **Recusar:** clique em \"Recusar\". A oferta é marcada como recusada. Pode enviar uma contraproposta.\n\nTodas as ações são registadas no histórico do tópico."
        },
        {
          "title": "Contrapropostas e negociação",
          "content": "Pode enviar uma **contraproposta** a qualquer momento, bastando enviar uma nova oferta com condições diferentes. A oferta anterior permanece visível no histórico, mas apenas a última oferta aceite é vinculativa."
        }
      ]
    },
    {
      "id": "subscriptions",
      "title": "Subscrições e pagamentos",
      "sections": [
        {
          "title": "Planos e níveis",
          "content": "A Surplify oferece vários níveis de subscrição:\n\n- **Basic** — funções essenciais para começar\n- **Professional** — funções avançadas, mais anúncios\n- **Enterprise** — conjunto completo de funções, incl. funcionalidade de grupo e acesso API\n\nAtualizações e mudanças para níveis inferiores estão disponíveis a qualquer momento no **Portal do vendedor** com faturação proporcional automática."
        },
        {
          "title": "Pagamentos via Stripe",
          "content": "Todos os pagamentos são processados em segurança através do **Stripe**:\n\n1. Escolha o nível pretendido\n2. É redirecionado para o Stripe Checkout\n3. Após o pagamento bem-sucedido, a sua subscrição é ativada instantaneamente\n\nMétodos suportados: cartão de crédito (Visa, Mastercard, Amex) e débito direto SEPA. As **faturas** estão no Portal do vendedor, em \"Faturas\"."
        },
        {
          "title": "Mandato de débito direto SEPA",
          "content": "Para pagamentos SEPA pode guardar o seu **IBAN e BIC**:\n\n1. Vá ao seu Portal (vendedor ou admin de grupo)\n2. Em \"Definições\" encontra os dados de pagamento\n3. Introduza o IBAN e o BIC\n4. Descarregue o seu mandato SEPA em PDF\n\nO mandato é gerado automaticamente a partir dos dados atuais da sua empresa e está totalmente em conformidade com o SEPA."
        },
        {
          "title": "Faturas e NIF/IVA",
          "content": "- As **faturas** são geradas automaticamente em PDF e podem ser descarregadas a qualquer momento\n- A **validação do número de IVA** é feita automaticamente através do sistema VIES da UE\n- O **endereço de faturação** pode ser editado nas definições do seu portal\n- A **autoliquidação (reverse charge)** é aplicada automaticamente quando é guardado um número de IVA da UE válido fora da Alemanha"
        }
      ]
    },
    {
      "id": "groups",
      "title": "Grupos e convites",
      "sections": [
        {
          "title": "Conceito de grupo",
          "content": "Os **grupos de empresa** permitem-lhe gerir várias instalações sob uma mesma estrutura:\n\n- Um **admin de grupo** gere todo o grupo\n- As **instalações** individuais são membros do grupo\n- Faturação partilhada e administração centralizada\n- Centros de custo opcionais por instalação"
        },
        {
          "title": "Convites",
          "content": "Como admin de grupo, pode convidar novos membros:\n\n1. Vá ao **Portal de admin de grupo** > \"Convites\"\n2. Escolha \"Convidar por e-mail\" ou \"Convidar por instalação\"\n3. O convidado recebe um e-mail com uma ligação\n4. Após a aceitação, a instalação junta-se ao grupo\n\n**Estado:** Pendente, Aceite, Recusado, Expirado, Revogado"
        },
        {
          "title": "Portal de admin de grupo",
          "content": "O Portal de admin de grupo oferece:\n\n- **Visão geral:** cartões de KPI, estado do grupo, detalhes do nível\n- **Membros:** lista de todas as instalações com edição de centros de custo\n- **Convites:** enviar e gerir convites\n- **Faturas:** faturas de grupo em PDF\n- **Definições:** endereço de faturação, número de IVA, dados SEPA"
        }
      ]
    },
    {
      "id": "sellerPortal",
      "title": "Portal do vendedor",
      "sections": [
        {
          "title": "Visão geral",
          "content": "No **Portal do vendedor** gere a sua subscrição e todos os detalhes financeiros:\n\n- **Subscrição:** estado, detalhes do nível, atualização/redução, cancelamento\n- **Faturas:** todas as faturas com descarga em PDF\n- **Comissões:** visão geral das receitas de comissões e histórico de transações\n- **Convites:** aceitar ou recusar convites de grupo pendentes\n- **Definições:** endereço de faturação, número de IVA, número de encomenda, dados SEPA"
        }
      ]
    },
    {
      "id": "externalPortal",
      "title": "Portal de fornecedores externos",
      "sections": [
        {
          "title": "O que é o portal externo?",
          "content": "O **portal externo** é uma área dedicada a fornecedores ou transformadores externos que pretendem responder a pedidos sem uma conta Surplify completa.\n\n- Conjunto de funções limitado: ver pedidos, submeter ofertas, trocar mensagens\n- Sem visibilidade de materiais ou grupos internos\n- Ideal para parceiros ocasionais"
        },
        {
          "title": "Registo externo",
          "content": "Os utilizadores externos podem registar-se através de uma **página de convite pública** ou de uma ligação de convite enviada por um gestor. Após o registo, a conta fica imediatamente utilizável, mas limitada às vistas externas."
        },
        {
          "title": "Cenários e acesso externo",
          "content": "Os admins podem controlar se e quando as empresas externas podem aceder aos pedidos através de **cenários**. A definição \"External Enabled\" na configuração do cenário ativa ou bloqueia globalmente o acesso externo."
        }
      ]
    },
    {
      "id": "commissions",
      "title": "Comissões",
      "sections": [
        {
          "title": "Como funcionam as comissões?",
          "content": "Por cada transação concluída com sucesso, a Surplify cobra uma **comissão percentual** sobre o preço de venda. A taxa depende do nível de subscrição:\n\n- Basic: taxa padrão\n- Professional: taxa reduzida\n- Enterprise: negociável individualmente"
        },
        {
          "title": "Painel de comissões",
          "content": "Em **\"Comissões\"** pode ver:\n\n- Os modelos de comissão atuais (ativos/históricos)\n- O histórico de transações com a comissão calculada por operação\n- Os totais mensais e os valores anuais acumulados\n- O estado de vencimento e os acertos\n\nOs admins podem criar, ativar e versionar modelos de comissão no painel de administração."
        }
      ]
    },
    {
      "id": "analytics",
      "title": "Análise",
      "sections": [
        {
          "title": "Painel de análise",
          "content": "O **painel de análise** dá a gestores e admins informações sobre:\n\n- Materiais em destaque (visualizações, pedidos, conversões)\n- Tendências de receita e transações\n- Taxas de acerto de correspondência\n- Atividade dos utilizadores por instalação/grupo\n\nTodos os gráficos podem ser filtrados por período (7/30/90 dias, personalizado)."
        },
        {
          "title": "Exportação e relatórios",
          "content": "Os resultados da análise podem ser exportados em **CSV ou Excel**. Para contabilidade está também disponível a **exportação DATEV** no painel de administração."
        }
      ]
    },
    {
      "id": "importing",
      "title": "Importação em massa (CSV/Excel)",
      "sections": [
        {
          "title": "Publicar materiais por carregamento",
          "content": "Os gestores e admins podem importar vários materiais de uma vez:\n\n1. Vá a **\"Importar\"**\n2. Descarregue o **modelo Excel** (mostra o formato de colunas esperado)\n3. Preencha o modelo com os seus dados\n4. Carregue o ficheiro\n5. Verifique os resultados da validação (os erros são assinalados por linha)\n6. Escolha um modo de importação:\n   - **Substituir tudo:** substitui os artigos existentes\n   - **Apenas enriquecer:** adiciona campos em falta sem substituir os existentes\n   - **Selecionar campos:** controlo detalhado sobre as colunas atualizadas\n7. Execute a importação"
        }
      ]
    },
    {
      "id": "scenarios",
      "title": "Cenários (admin)",
      "sections": [
        {
          "title": "Modos de cenário",
          "content": "Os admins orientam o comportamento global da plataforma através de **cenários**:\n\n- **Equilibrado (predefinição):** regras padrão para a visibilidade interna e externa\n- **Conservador:** visibilidade interna mais longa, aprovação mais rigorosa\n- **Aggressive Cash:** externalização rápida, alcance máximo\n- **Modo crise:** acesso externo bloqueado, apenas operações internas\n\nParâmetros como a \"janela interna\" (dias de visibilidade apenas interna) e os \"dias de escalonamento\" (notificação automática para pedidos antigos) são configuráveis."
        }
      ]
    },
    {
      "id": "bugReports",
      "title": "Reportar problemas",
      "sections": [
        {
          "title": "Submeter um relatório de erro",
          "content": "Se detetar um erro:\n\n1. Clique no **ícone de inseto** no cabeçalho ou abra **\"Os meus relatórios de erro\"**\n2. Descreva o problema com a maior precisão possível (URL, passos, comportamento esperado)\n3. Anexe uma captura de ecrã (opcional, mas recomendado)\n4. Submeta\n\nO seguimento de estado mostra se a equipa confirmou (\"Aberto\"), está a trabalhar (\"Em curso\") ou resolveu (\"Concluído\") o relatório."
        }
      ]
    },
    {
      "id": "notifications",
      "title": "Notificações",
      "sections": [
        {
          "title": "Configurar notificações por e-mail",
          "content": "A Surplify envia e-mails automáticos para eventos importantes:\n\n- Novos pedidos e ofertas\n- **Aceitação de oferta** (comprador + vendedor recebem cada um uma confirmação)\n- Correspondências bem-sucedidas\n- Pagamentos e lembretes\n- Convites e alterações de grupo\n- Escalonamentos (pedidos antigos não tratados)\n\nEm **\"Notificações por e-mail\"** nas definições pode ativar ou desativar cada tipo de evento individualmente."
        }
      ]
    },
    {
      "id": "developerApi",
      "title": "Programador: API, webhooks, SDK e CLI",
      "sections": [
        {
          "title": "API REST pública",
          "content": "A Surplify oferece uma **API REST** completa (v1 e v2) para integração nos seus sistemas ERP ou logísticos:\n\n- URL base: `https://api.surplify.de/api/v1/`\n- Autenticação através de **chave API** (crie-a no portal de programadores)\n- Limites de taxa por chave\n- Especificação OpenAPI disponível\n\nA referência interativa da API está em **\"Programadores\" > Documentação da API**."
        },
        {
          "title": "Webhooks de saída (assinados com HMAC)",
          "content": "Receba eventos em tempo real nos seus sistemas:\n\n1. Crie um endpoint de webhook no portal de programadores\n2. Subscreva eventos (por ex. `material.created`, `transaction.completed`, `offer.accepted`)\n3. Cada payload é **assinado em HMAC-SHA256** (cabeçalho `X-Surplify-Signature`)\n4. Repetições automáticas com recuo exponencial em caso de falha de entrega\n\nValide as assinaturas com o algoritmo documentado na API para evitar adulterações."
        },
        {
          "title": "SDK de cliente (Python, Node.js, PHP)",
          "content": "Estão disponíveis para descarga SDK gerados dinamicamente:\n\n- **SDK Python** (pacote pip)\n- **SDK Node.js** (pacote npm)\n- **SDK PHP** (Composer)\n\nOs SDK vêm pré-configurados com gestão de autenticação, repetições por limite de taxa e modelos tipados. Descarregue-os no portal de programadores."
        },
        {
          "title": "CLI: npx create-surplify-app",
          "content": "A **ferramenta oficial de scaffolding** permite-lhe começar em segundos:\n\n```bash\nnpx create-surplify-app my-integration\ncd my-integration\nnpm install\nnpm run dev\n```\n\nO projeto gerado inclui:\n- Um listener de webhook pré-configurado com validação HMAC\n- Exemplos de chamadas à API (listar materiais, enviar pedidos)\n- Um modelo `.env` para a chave API, o segredo do webhook e o URL base\n- Testes e recarregamento a quente\n\nPerfeito para criar integrações em minutos em vez de dias."
        }
      ]
    },
    {
      "id": "adminOverview",
      "title": "Visão geral do portal de administração",
      "sections": [
        {
          "title": "O que pode fazer um admin de plataforma?",
          "content": "Os admins de plataforma têm acesso a todas as camadas administrativas:\n\n- **Utilizadores e funções:** criar, suspender, alterar funções\n- **Subscrições:** ajustes manuais, créditos de boa vontade\n- **Comissões:** criar e versionar modelos\n- **Mandatos SEPA:** inspecionar e aprovar\n- **Faturas:** todas as faturas de toda a plataforma\n- **Exportação DATEV:** gerar dados contabilísticos\n- **Cenários:** orientar o comportamento global da plataforma\n- **Registo de auditoria:** acompanhar cada ação relevante para a segurança"
        }
      ]
    }
  ],

  "guide.faq": [
    {
      "category": "Geral",
      "questions": [
        { "q": "O que é a Surplify?", "a": "A Surplify é um marketplace B2B para materiais de embalagem. As empresas podem publicar materiais excedentes ou procurar os materiais de que precisam." },
        { "q": "Quem pode usar a Surplify?", "a": "A Surplify destina-se a empresas do setor da embalagem: comerciantes, fabricantes e transformadores. Tanto as pequenas como as grandes empresas beneficiam da plataforma." },
        { "q": "A utilização é paga?", "a": "O registo é gratuito. Para funções avançadas como mais anúncios, correspondência ou acesso à API estão disponíveis níveis pagos." },
        { "q": "Em que idiomas está a Surplify disponível?", "a": "Sete idiomas: alemão, inglês, espanhol, francês, italiano, português e romeno. Mude através do globo no cabeçalho." }
      ]
    },
    {
      "category": "Conta e registo",
      "questions": [
        { "q": "Como crio uma conta?", "a": "Clique em \"Registar\", preencha o formulário e confirme o seu endereço de e-mail. Pode começar de imediato." },
        { "q": "Posso alterar o meu endereço de e-mail?", "a": "Contacte o suporte para alterar o seu e-mail. Por motivos de segurança, não é possível fazê-lo por conta própria." },
        { "q": "O que acontece se esquecer a palavra-passe?", "a": "Use a função \"Esqueci-me da palavra-passe\" na página de início de sessão. Receberá um e-mail com uma ligação para redefinir." },
        { "q": "Que funções existem?", "a": "Comerciante, Gestor (admin de uma instalação), Admin de grupo (gere várias instalações), Admin de plataforma e fornecedores externos. A função determina quais os itens de menu visíveis." }
      ]
    },
    {
      "category": "Comércio e materiais",
      "questions": [
        { "q": "Como publico um material?", "a": "Clique em \"Publicar\", preencha o formulário com todos os detalhes relevantes e publique. Aparece de imediato no mercado." },
        { "q": "Como funciona a correspondência?", "a": "O motor de correspondência compara automaticamente anúncios e procuras com base no tipo de material, quantidade e localização. Ambas as partes são notificadas quando há correspondência." },
        { "q": "Posso importar materiais em massa?", "a": "Sim, gestores e admins podem usar a função de importação para publicar vários materiais de uma vez através de carregamento Excel (com validação e simulação)." },
        { "q": "O que são procuras?", "a": "As procuras são pedidos de compra ativos. Quando precisa de um material específico, cria uma procura. Os vendedores podem responder com ofertas." },
        { "q": "Para que serve a lista de favoritos?", "a": "A lista de favoritos guarda os materiais que pretende acompanhar sem enviar um pedido. Pode receber alertas opcionais sobre alterações de preço ou estado." }
      ]
    },
    {
      "category": "Mensagens e ofertas",
      "questions": [
        { "q": "Como envio uma oferta vinculativa?", "a": "Abra o tópico de mensagens da transação, clique em \"Enviar oferta\" e introduza preço, quantidade e condições. A oferta fica destacada no histórico da conversa." },
        { "q": "O que acontece quando uma oferta é aceite?", "a": "A transação é atualizada automaticamente para o preço acordado e ambas as partes recebem uma confirmação por e-mail com todos os detalhes (quantidade, preço, contacto da contraparte)." },
        { "q": "Posso retirar uma oferta?", "a": "Sim, enquanto o destinatário não tiver aceitado, pode clicar em \"Retirar\" no tópico. Após a aceitação, a oferta é vinculativa." },
        { "q": "Como funciona uma contraproposta?", "a": "Basta enviar uma nova oferta com condições diferentes. A anterior permanece visível no histórico, mas apenas a última oferta aceite é vinculativa." }
      ]
    },
    {
      "category": "Pagamentos e faturas",
      "questions": [
        { "q": "Que métodos de pagamento são suportados?", "a": "Os pagamentos são processados via Stripe. São suportados cartão de crédito (Visa, Mastercard, Amex) e débito direto SEPA." },
        { "q": "Onde encontro as minhas faturas?", "a": "No Portal do vendedor em \"Faturas\" ou no Portal de admin de grupo. Todas as faturas podem ser descarregadas em PDF." },
        { "q": "O que é um mandato SEPA?", "a": "O mandato SEPA autoriza a Surplify a cobrar pagamentos da sua conta bancária. Pode gerá-lo nas definições depois de guardar o seu IBAN/BIC." },
        { "q": "Como é validado o meu número de IVA?", "a": "A Surplify valida automaticamente o seu número de IVA através do sistema VIES da UE. Introduza-o nas definições do seu portal." },
        { "q": "A autoliquidação é aplicada?", "a": "Sim, automaticamente para um número de IVA da UE válido fora da Alemanha. A fatura inclui então a indicação correspondente." }
      ]
    },
    {
      "category": "Grupos e instalações",
      "questions": [
        { "q": "O que é um grupo de empresa?", "a": "Um grupo de empresa agrupa várias instalações de uma mesma organização. O grupo é gerido e faturado de forma centralizada." },
        { "q": "Como convido instalações para o meu grupo?", "a": "No Portal de admin de grupo em \"Convites\". Pode convidar por e-mail ou através de pesquisa de instalações. Os convites expiram após algum tempo." },
        { "q": "Uma instalação pode pertencer a vários grupos?", "a": "Não, uma instalação só pode pertencer a um grupo de cada vez." }
      ]
    },
    {
      "category": "Portal externo",
      "questions": [
        { "q": "A quem se destina o portal externo?", "a": "A parceiros externos ocasionais que pretendem responder a pedidos ou submeter ofertas sem uma conta completa." },
        { "q": "Como me registo como fornecedor externo?", "a": "Através de uma ligação de convite de um gestor ou da página de registo pública para fornecedores externos." },
        { "q": "Posso passar de uma conta externa para uma completa?", "a": "Sim, a atualização é possível: contacte o suporte ou o gestor que o convidou." }
      ]
    },
    {
      "category": "Administração",
      "questions": [
        { "q": "O que pode fazer um admin de plataforma?", "a": "Gestão de utilizadores, gestão de subscrições, comissões, mandatos SEPA, faturas, exportação DATEV, cenários, registo de auditoria e todos os painéis analíticos." },
        { "q": "O que é a exportação DATEV?", "a": "A exportação DATEV gera dados contabilísticos em formato DATEV para o contabilista. Está disponível no painel de administração (separador Exportações)." },
        { "q": "Como giro as comissões?", "a": "No painel de comissões os admins podem configurar modelos de comissão, rever transações e criar acertos." },
        { "q": "O que é o registo de auditoria?", "a": "Um registo imutável de cada ação relevante para a segurança (inícios de sessão, alterações de função, ajustes de faturas), disponível no painel de administração." }
      ]
    },
    {
      "category": "Programador e API",
      "questions": [
        { "q": "Como obtenho uma chave API?", "a": "No portal de programadores em \"Chaves API\" pode criar uma nova chave. Guarde-a em segurança: só é mostrada uma vez." },
        { "q": "Existem limites de taxa?", "a": "Sim, por chave API. Os limites exatos são visíveis no portal de programadores e dependem do seu nível. Ao exceder, recebe um HTTP 429." },
        { "q": "Como valido as assinaturas dos webhooks?", "a": "Cada payload é assinado com HMAC-SHA256 (cabeçalho X-Surplify-Signature). Use o seu segredo de webhook para validar; há exemplos de código na documentação da API." },
        { "q": "O que é o create-surplify-app?", "a": "Uma ferramenta oficial de scaffolding (npx create-surplify-app my-integration) que cria um projeto de integração pré-configurado com listener de webhook, cliente API e testes em segundos." },
        { "q": "Que SDK estão disponíveis?", "a": "Python, Node.js e PHP. Todos são gerados dinamicamente e incluem gestão de autenticação, repetições e modelos tipados." }
      ]
    },
    {
      "category": "Suporte e idiomas",
      "questions": [
        { "q": "Como reporto um erro?", "a": "Clique no ícone de inseto no cabeçalho ou abra \"Os meus relatórios de erro\". Descreva o problema e anexe opcionalmente uma captura de ecrã." },
        { "q": "Como mudo o idioma?", "a": "No canto superior direito do cabeçalho encontra o globo: clique nele e selecione o seu idioma. A sua escolha é guardada." },
        { "q": "Como contacto o suporte?", "a": "Para assuntos urgentes use os contactos nas informações legais ou escreva diretamente a partir do portal (\"Ajuda > Contactar suporte\")." }
      ]
    }
  ]
};

export const guideContentRO = {
  "guide.title": "Ajutor și întrebări frecvente",
  "guide.subtitle": "Manual de utilizare și răspunsuri la întrebările frecvente",
  "guide.searchPlaceholder": "Caută în manual și întrebări frecvente...",
  "guide.tabGuide": "Manual de utilizare",
  "guide.tabFaq": "Întrebări frecvente",
  "guide.noResults": "Niciun rezultat pentru",
  "guide.topic": "subiect",
  "guide.topics": "subiecte",
  "guide.vote.helpful": "Acest articol a fost util?",
  "guide.vote.up": "Util",
  "guide.vote.down": "Neutil",
  "guide.vote.thanksUp": "Mulțumim pentru feedback!",
  "guide.vote.thanksDown": "Mulțumim — vom îmbunătăți articolul.",
  "guide.vote.failed": "Trimiterea feedbackului a eșuat",

  "guide.categories": [
    {
      "id": "gettingStarted",
      "title": "Primii pași",
      "sections": [
        {
          "title": "Înregistrare și autentificare",
          "content": "Pentru a folosi Surplify, înregistrează-te cu e-mailul tău profesional. După înregistrare, obții acces la panoul tău personal.\n\n**Cum te înregistrezi:**\n1. Apasă pe „Înregistrare\" în colțul din dreapta sus\n2. Introdu numele, e-mailul și parola\n3. Alege rolul tău (Comerciant, Manager etc.)\n4. După înregistrare ești conectat automat\n\n**Autentificare:** folosește e-mailul înregistrat și parola pe pagina de autentificare."
        },
        {
          "title": "Prezentare generală a navigării",
          "content": "Meniul principal se află în colțul din dreapta sus, sub numele tău de utilizator. Este organizat în:\n\n- **Piață** — articolele tale, lista de urmărire, cereri, materiale standard\n- **Comerț și comunicare** — solicitări, tranzacții, potrivire, mesaje\n- **Administrare** — panou, analiză, import (doar manageri/administratori)\n- **Portaluri** — portal vânzător, portal admin de grup, portal extern\n- **Dezvoltatori** — API, webhook-uri, SDK-uri, CLI\n- **Setări** — notificări, ajutor, limbă"
        },
        {
          "title": "Schimbarea limbii",
          "content": "Surplify este disponibil în mai multe limbi (germană, engleză, spaniolă, franceză, italiană, portugheză, română).\n\n**Pentru a schimba limba:**\n1. Apasă pe selectorul de limbă (pictograma glob) din antet\n2. Selectează limba dorită\n3. Întreaga interfață este tradusă instantaneu\n\nAlegerea ta este salvată în browser și restabilită la următoarea vizită."
        }
      ]
    },
    {
      "id": "marketplace",
      "title": "Piață",
      "sections": [
        {
          "title": "Publicarea unui material",
          "content": "Pentru a publica un material pe piață:\n\n1. Apasă pe **„Publică\"** în navigarea de sus\n2. Completează formularul: nume, categorie, descriere, cantitate, preț\n3. Adaugă imagini și specificații opționale\n4. Apasă pe „Publică\"\n\n**Sfaturi:** descrierile detaliate și imaginile cresc vizibilitatea."
        },
        {
          "title": "Căutarea și găsirea materialelor",
          "content": "Folosește bara de căutare centrală pentru a găsi materiale. Poți căuta după:\n\n- **Numele materialului** sau **categorie**\n- **Filtre:** tip, preț, cantitate, locație\n- Sortarea rezultatelor după relevanță, preț sau dată\n\nPaginarea și sortarea (dată, preț, cantitate) sunt disponibile pentru seturi mari de rezultate."
        },
        {
          "title": "Listă de urmărire",
          "content": "**Lista de urmărire** îți permite să ții sub observație materialele interesante fără a trimite imediat o solicitare.\n\n**Cum funcționează:**\n1. Apasă pe **pictograma inimă** de pe un card sau pagina de detaliu a unui material\n2. Materialul este adăugat în lista ta personală de urmărire\n3. Deschide „Lista de urmărire\" din meniu pentru a vedea toate articolele salvate\n\n**Notificări:** poți opta să primești alerte când prețul scade sau când se schimbă starea unui material urmărit."
        },
        {
          "title": "Crearea cererilor (anunțuri de cerere)",
          "content": "În **„Cereri\"** poți publica solicitări de achiziție active:\n\n1. Mergi la Cereri\n2. Creează o cerere nouă cu tip, cantitate și buget\n3. Vânzătorii pot vedea cererile tale și pot răspunde\n\nCererile active sunt luate în considerare și de motorul de potrivire."
        },
        {
          "title": "Materiale standard",
          "content": "Lista de materiale standard conține tipuri de material predefinite pentru organizația ta. Acestea servesc drept șablon la crearea anunțurilor, astfel încât inventarul să rămână coerent. Managerii pot gestiona lista centralizat."
        }
      ]
    },
    {
      "id": "trading",
      "title": "Comerț și tranzacții",
      "sections": [
        {
          "title": "Trimiterea și gestionarea solicitărilor",
          "content": "Dacă te interesează un material:\n\n1. Deschide pagina de detaliu a materialului\n2. Apasă pe **„Trimite solicitare\"**\n3. Introdu cantitatea dorită și un comentariu opțional\n4. Solicitarea este trimisă vânzătorului\n\n**Solicitările primite** se află în „Solicitări\" din meniul tău, cu filtre de stare (Deschisă, Acceptată, Respinsă)."
        },
        {
          "title": "Tranzacții",
          "content": "În **„Tranzacții\"** vezi toate operațiunile în curs și finalizate:\n\n- **Stare:** Deschisă, În negociere, Finalizată, Anulată\n- **Detalii:** cantitate, preț, info cumpărător/vânzător, atașamente\n- Fiecare tranzacție poate fi comentată și generează automat un fir de mesaje"
        },
        {
          "title": "Potrivire inteligentă",
          "content": "**Motorul de potrivire** compară automat anunțurile tale cu cererile și invers:\n\n- Potrivirile sunt calculate în timp real (tip de material, cantitate, regiune, interval de preț)\n- Primești alerte prin e-mail la noile potriviri\n- Apasă pe o potrivire pentru a începe direct o solicitare sau un mesaj\n\nPanoul de potrivire (manager/admin) afișează rata de succes și cele mai bune potriviri."
        }
      ]
    },
    {
      "id": "messaging",
      "title": "Mesaje și oferte",
      "sections": [
        {
          "title": "Fire de mesaje",
          "content": "**Sistemul de mesagerie** integrat permite comunicarea directă 1:1 între cumpărător și vânzător:\n\n- Fiecare tranzacție generează automat un fir\n- Mesajele necitite sunt afișate ca insignă în meniu\n- Poți atașa fișiere (specificații, fotografii) la mesaje"
        },
        {
          "title": "Trimiterea ofertelor",
          "content": "În orice fir poți trimite o **ofertă obligatorie**:\n\n1. Deschide firul\n2. Apasă pe **„Trimite ofertă\"**\n3. Introdu prețul, cantitatea și condiții opționale (termen de ridicare, condiții de plată)\n4. Oferta apare ca mesaj evidențiat cu butoanele „Acceptă\" / „Respinge\"\n\nOfertele au o stare clară: **Deschisă**, **Acceptată**, **Respinsă** sau **Retrasă**."
        },
        {
          "title": "Acceptarea sau respingerea unei oferte",
          "content": "Ca destinatar al unei oferte:\n\n- **Acceptă:** apasă pe „Acceptă\". Tranzacția este actualizată automat la prețul convenit, iar ambele părți primesc o **confirmare automată prin e-mail** cu toate detaliile (cantitate, preț, partener, contact).\n- **Respinge:** apasă pe „Respinge\". Oferta este marcată ca respinsă. Poți trimite o contraofertă.\n\nToate acțiunile sunt înregistrate în istoricul firului."
        },
        {
          "title": "Contraoferte și negociere",
          "content": "Poți trimite o **contraofertă** oricând, pur și simplu trimițând o nouă ofertă cu condiții diferite. Oferta anterioară rămâne vizibilă în istoric, dar numai ultima ofertă acceptată este obligatorie."
        }
      ]
    },
    {
      "id": "subscriptions",
      "title": "Abonamente și plăți",
      "sections": [
        {
          "title": "Planuri și niveluri",
          "content": "Surplify oferă mai multe niveluri de abonament:\n\n- **Basic** — funcții esențiale pentru a începe\n- **Professional** — funcții avansate, mai multe anunțuri\n- **Enterprise** — set complet de funcții, incl. funcționalitate de grup și acces API\n\nUpgrade-urile și downgrade-urile sunt disponibile oricând din **Portalul vânzătorului**, cu facturare pro-rata automată."
        },
        {
          "title": "Plăți prin Stripe",
          "content": "Toate plățile se efectuează în siguranță prin **Stripe**:\n\n1. Alege nivelul dorit\n2. Ești redirecționat către Stripe Checkout\n3. După plata reușită, abonamentul tău este activat instantaneu\n\nMetode acceptate: card de credit (Visa, Mastercard, Amex) și debit direct SEPA. **Facturile** sunt disponibile în Portalul vânzătorului, la „Facturi\"."
        },
        {
          "title": "Mandat de debit direct SEPA",
          "content": "Pentru plățile SEPA poți salva **IBAN-ul și BIC-ul**:\n\n1. Mergi la Portalul tău (vânzător sau admin de grup)\n2. La „Setări\" găsești datele de plată\n3. Introdu IBAN-ul și BIC-ul\n4. Descarcă mandatul SEPA în format PDF\n\nMandatul este generat automat din datele actuale ale companiei tale și este pe deplin conform SEPA."
        },
        {
          "title": "Facturi și cod TVA",
          "content": "- **Facturile** sunt generate automat în PDF și pot fi descărcate oricând\n- **Validarea codului TVA** se face automat prin sistemul VIES al UE\n- **Adresa de facturare** poate fi editată în setările portalului tău\n- **Taxarea inversă** se aplică automat când este salvat un cod TVA UE valid din afara Germaniei"
        }
      ]
    },
    {
      "id": "groups",
      "title": "Grupuri și invitații",
      "sections": [
        {
          "title": "Conceptul de grup",
          "content": "**Grupurile de companii** îți permit să gestionezi mai multe locații sub o singură structură:\n\n- Un **admin de grup** gestionează întregul grup\n- **Locațiile** individuale sunt membre ale grupului\n- Facturare comună și administrare centralizată\n- Centre de cost opționale pe locație"
        },
        {
          "title": "Invitații",
          "content": "Ca admin de grup poți invita membri noi:\n\n1. Mergi la **Portalul admin de grup** > „Invitații\"\n2. Alege „Invită prin e-mail\" sau „Invită după locație\"\n3. Cel invitat primește un e-mail cu un link\n4. După acceptare, locația se alătură grupului\n\n**Stare:** În așteptare, Acceptată, Respinsă, Expirată, Revocată"
        },
        {
          "title": "Portalul admin de grup",
          "content": "Portalul admin de grup oferă:\n\n- **Prezentare generală:** carduri KPI, starea grupului, detalii despre nivel\n- **Membri:** lista tuturor locațiilor cu editarea centrelor de cost\n- **Invitații:** trimiterea și gestionarea invitațiilor\n- **Facturi:** facturi de grup în PDF\n- **Setări:** adresă de facturare, cod TVA, date SEPA"
        }
      ]
    },
    {
      "id": "sellerPortal",
      "title": "Portalul vânzătorului",
      "sections": [
        {
          "title": "Prezentare generală",
          "content": "În **Portalul vânzătorului** îți gestionezi abonamentul și toate detaliile financiare:\n\n- **Abonament:** stare, detalii despre nivel, upgrade/downgrade, anulare\n- **Facturi:** toate facturile cu descărcare în PDF\n- **Comisioane:** prezentare generală a veniturilor din comisioane și istoricul tranzacțiilor\n- **Invitații:** acceptarea sau respingerea invitațiilor de grup în așteptare\n- **Setări:** adresă de facturare, cod TVA, număr de comandă, date SEPA"
        }
      ]
    },
    {
      "id": "externalPortal",
      "title": "Portalul furnizorilor externi",
      "sections": [
        {
          "title": "Ce este portalul extern?",
          "content": "**Portalul extern** este o zonă dedicată furnizorilor sau procesatorilor externi care doresc să răspundă la solicitări fără un cont Surplify complet.\n\n- Set de funcții limitat: vizualizarea solicitărilor, trimiterea ofertelor, schimbul de mesaje\n- Fără vizibilitate asupra materialelor sau grupurilor interne\n- Ideal pentru parteneri ocazionali"
        },
        {
          "title": "Înregistrare externă",
          "content": "Utilizatorii externi se pot înregistra printr-o **pagină publică de invitație** sau printr-un link de invitație trimis de un manager. După înregistrare, contul este utilizabil imediat, dar limitat la vizualizările externe."
        },
        {
          "title": "Scenarii și acces extern",
          "content": "Administratorii pot controla dacă și când companiile externe pot accesa solicitările prin **scenarii**. Setarea „External Enabled\" din configurația scenariului activează sau blochează accesul extern la nivel global."
        }
      ]
    },
    {
      "id": "commissions",
      "title": "Comisioane",
      "sections": [
        {
          "title": "Cum funcționează comisioanele?",
          "content": "Pentru fiecare tranzacție încheiată cu succes, Surplify percepe un **comision procentual** asupra prețului de vânzare. Rata depinde de nivelul de abonament:\n\n- Basic: rată standard\n- Professional: rată redusă\n- Enterprise: negociabilă individual"
        },
        {
          "title": "Panoul de comisioane",
          "content": "În **„Comisioane\"** poți vedea:\n\n- Modelele actuale de comisioane (active/istorice)\n- Istoricul tranzacțiilor cu comisionul calculat pe operațiune\n- Totalurile lunare și cifrele anuale cumulate\n- Starea de scadență și decontările\n\nAdministratorii pot crea, activa și versiona modele de comisioane în panoul de administrare."
        }
      ]
    },
    {
      "id": "analytics",
      "title": "Analiză",
      "sections": [
        {
          "title": "Panoul de analiză",
          "content": "**Panoul de analiză** oferă managerilor și administratorilor informații despre:\n\n- Materiale de top (vizualizări, solicitări, conversii)\n- Tendințele veniturilor și tranzacțiilor\n- Ratele de succes ale potrivirii\n- Activitatea utilizatorilor pe locație/grup\n\nToate graficele pot fi filtrate după perioadă (7/30/90 zile, personalizat)."
        },
        {
          "title": "Export și raportare",
          "content": "Rezultatele analizei pot fi exportate în **CSV sau Excel**. Pentru contabilitate este disponibil și **exportul DATEV** în panoul de administrare."
        }
      ]
    },
    {
      "id": "importing",
      "title": "Import în masă (CSV/Excel)",
      "sections": [
        {
          "title": "Publicarea materialelor prin încărcare",
          "content": "Managerii și administratorii pot importa mai multe materiale simultan:\n\n1. Mergi la **„Import\"**\n2. Descarcă **șablonul Excel** (arată formatul de coloane așteptat)\n3. Completează șablonul cu datele tale\n4. Încarcă fișierul\n5. Verifică rezultatele validării (erorile sunt marcate pe rând)\n6. Alege un mod de import:\n   - **Suprascrie tot:** înlocuiește articolele existente\n   - **Doar îmbogățire:** adaugă câmpurile lipsă fără a le suprascrie pe cele existente\n   - **Selectează câmpuri:** control detaliat asupra coloanelor actualizate\n7. Execută importul"
        }
      ]
    },
    {
      "id": "scenarios",
      "title": "Scenarii (admin)",
      "sections": [
        {
          "title": "Moduri de scenariu",
          "content": "Administratorii dirijează comportamentul global al platformei prin **scenarii**:\n\n- **Echilibrat (implicit):** reguli standard pentru vizibilitatea internă și externă\n- **Conservator:** vizibilitate internă mai lungă, aprobare mai strictă\n- **Aggressive Cash:** externalizare rapidă, acoperire maximă\n- **Mod criză:** acces extern blocat, doar operațiuni interne\n\nParametri precum „fereastra internă\" (zile de vizibilitate doar internă) și „zilele de escaladare\" (notificare automată pentru solicitări vechi) sunt configurabili."
        }
      ]
    },
    {
      "id": "bugReports",
      "title": "Raportarea problemelor",
      "sections": [
        {
          "title": "Trimiterea unui raport de eroare",
          "content": "Dacă observi o eroare:\n\n1. Apasă pe **pictograma insectă** din antet sau deschide **„Rapoartele mele de eroare\"**\n2. Descrie problema cât mai precis posibil (URL, pași, comportament așteptat)\n3. Atașează o captură de ecran (opțional, dar recomandat)\n4. Trimite\n\nUrmărirea stării arată dacă echipa a confirmat („Deschis\"), lucrează („În curs\") sau a rezolvat („Finalizat\") raportul."
        }
      ]
    },
    {
      "id": "notifications",
      "title": "Notificări",
      "sections": [
        {
          "title": "Configurarea notificărilor prin e-mail",
          "content": "Surplify trimite e-mailuri automate pentru evenimentele cheie:\n\n- Solicitări și oferte noi\n- **Acceptarea ofertei** (cumpărătorul + vânzătorul primesc fiecare o confirmare)\n- Potriviri reușite\n- Plăți și mementouri\n- Invitații și modificări de grup\n- Escaladări (solicitări vechi netratate)\n\nÎn **„Notificări prin e-mail\"** din setări poți activa sau dezactiva fiecare tip de eveniment individual."
        }
      ]
    },
    {
      "id": "developerApi",
      "title": "Dezvoltator: API, webhook-uri, SDK-uri și CLI",
      "sections": [
        {
          "title": "API REST public",
          "content": "Surplify oferă un **API REST** complet (v1 și v2) pentru integrarea în sistemele tale ERP sau logistice:\n\n- URL de bază: `https://api.surplify.de/api/v1/`\n- Autentificare prin **cheie API** (creează-o în portalul pentru dezvoltatori)\n- Limite de rată pe cheie\n- Specificație OpenAPI disponibilă\n\nReferința interactivă a API-ului se află la **„Dezvoltatori\" > Documentația API**."
        },
        {
          "title": "Webhook-uri de ieșire (semnate HMAC)",
          "content": "Primește evenimente în timp real în sistemele tale:\n\n1. Creează un endpoint de webhook în portalul pentru dezvoltatori\n2. Abonează-te la evenimente (de ex. `material.created`, `transaction.completed`, `offer.accepted`)\n3. Fiecare payload este **semnat HMAC-SHA256** (antet `X-Surplify-Signature`)\n4. Reîncercări automate cu backoff exponențial în caz de eșec la livrare\n\nValidează semnăturile cu algoritmul documentat în API pentru a preveni falsificarea."
        },
        {
          "title": "SDK-uri client (Python, Node.js, PHP)",
          "content": "Sunt disponibile pentru descărcare SDK-uri generate dinamic:\n\n- **SDK Python** (pachet pip)\n- **SDK Node.js** (pachet npm)\n- **SDK PHP** (Composer)\n\nSDK-urile vin preconfigurate cu gestionarea autentificării, reîncercări la limita de rată și modele tipizate. Descarcă-le din portalul pentru dezvoltatori."
        },
        {
          "title": "CLI: npx create-surplify-app",
          "content": "**Instrumentul oficial de scaffolding** te ajută să începi în câteva secunde:\n\n```bash\nnpx create-surplify-app my-integration\ncd my-integration\nnpm install\nnpm run dev\n```\n\nProiectul generat include:\n- Un listener de webhook preconfigurat cu validare HMAC\n- Exemple de apeluri API (listarea materialelor, trimiterea solicitărilor)\n- Un șablon `.env` pentru cheia API, secretul webhook-ului și URL-ul de bază\n- Teste și reîncărcare la cald\n\nPerfect pentru a construi integrări în minute, nu în zile."
        }
      ]
    },
    {
      "id": "adminOverview",
      "title": "Prezentare generală a portalului de administrare",
      "sections": [
        {
          "title": "Ce poate face un admin de platformă?",
          "content": "Administratorii de platformă au acces la toate nivelurile administrative:\n\n- **Utilizatori și roluri:** creare, suspendare, schimbarea rolurilor\n- **Abonamente:** ajustări manuale, credite de bunăvoință\n- **Comisioane:** crearea și versionarea modelelor\n- **Mandate SEPA:** inspectare și aprobare\n- **Facturi:** toate facturile la nivelul întregii platforme\n- **Export DATEV:** generarea datelor contabile\n- **Scenarii:** dirijarea comportamentului global al platformei\n- **Jurnal de audit:** urmărirea fiecărei acțiuni relevante pentru securitate"
        }
      ]
    }
  ],

  "guide.faq": [
    {
      "category": "General",
      "questions": [
        { "q": "Ce este Surplify?", "a": "Surplify este o piață B2B pentru materiale de ambalaj. Companiile pot publica materiale excedentare sau pot căuta materialele de care au nevoie." },
        { "q": "Cine poate folosi Surplify?", "a": "Surplify se adresează companiilor din industria ambalajelor: comercianți, producători și procesatori. Atât companiile mici, cât și cele mari beneficiază de platformă." },
        { "q": "Utilizarea este cu plată?", "a": "Înregistrarea este gratuită. Pentru funcții avansate precum mai multe anunțuri, potrivire sau acces API sunt disponibile niveluri cu plată." },
        { "q": "În ce limbi este disponibil Surplify?", "a": "Șapte limbi: germană, engleză, spaniolă, franceză, italiană, portugheză și română. Schimbă prin globul din antet." }
      ]
    },
    {
      "category": "Cont și înregistrare",
      "questions": [
        { "q": "Cum îmi creez un cont?", "a": "Apasă pe „Înregistrare\", completează formularul și confirmă adresa de e-mail. Poți începe imediat." },
        { "q": "Pot să îmi schimb adresa de e-mail?", "a": "Contactează asistența pentru a-ți schimba e-mailul. Din motive de securitate, acest lucru nu poate fi făcut de unul singur." },
        { "q": "Ce se întâmplă dacă îmi uit parola?", "a": "Folosește funcția „Am uitat parola\" de pe pagina de autentificare. Vei primi un e-mail cu un link de resetare." },
        { "q": "Ce roluri există?", "a": "Comerciant, Manager (admin al unei locații), Admin de grup (gestionează mai multe locații), Admin de platformă și furnizori externi. Rolul determină ce elemente de meniu sunt vizibile." }
      ]
    },
    {
      "category": "Comerț și materiale",
      "questions": [
        { "q": "Cum public un material?", "a": "Apasă pe „Publică\", completează formularul cu toate detaliile relevante și publică. Apare imediat pe piață." },
        { "q": "Cum funcționează potrivirea?", "a": "Motorul de potrivire compară automat anunțurile și cererile în funcție de tipul de material, cantitate și locație. Ambele părți sunt notificate la o potrivire." },
        { "q": "Pot importa materiale în masă?", "a": "Da, managerii și administratorii pot folosi funcția de import pentru a publica mai multe materiale simultan prin încărcare Excel (cu validare și rulare de test)." },
        { "q": "Ce sunt cererile?", "a": "Cererile sunt solicitări de achiziție active. Când ai nevoie de un anumit material, creezi o cerere. Vânzătorii pot răspunde cu oferte." },
        { "q": "La ce folosește lista de urmărire?", "a": "Lista de urmărire salvează materialele pe care vrei să le urmărești fără a trimite o solicitare. Poți primi alerte opționale despre modificările de preț sau stare." }
      ]
    },
    {
      "category": "Mesaje și oferte",
      "questions": [
        { "q": "Cum trimit o ofertă obligatorie?", "a": "Deschide firul de mesaje al tranzacției, apasă pe „Trimite ofertă\" și introdu prețul, cantitatea și condițiile. Oferta este evidențiată în istoricul conversației." },
        { "q": "Ce se întâmplă când o ofertă este acceptată?", "a": "Tranzacția este actualizată automat la prețul convenit, iar ambele părți primesc o confirmare prin e-mail cu toate detaliile (cantitate, preț, contactul partenerului)." },
        { "q": "Pot retrage o ofertă?", "a": "Da, atât timp cât destinatarul nu a acceptat încă, poți apăsa pe „Retrage\" în fir. După acceptare, oferta este obligatorie." },
        { "q": "Cum funcționează o contraofertă?", "a": "Trimite pur și simplu o nouă ofertă cu condiții diferite. Cea anterioară rămâne vizibilă în istoric, dar numai ultima ofertă acceptată este obligatorie." }
      ]
    },
    {
      "category": "Plăți și facturi",
      "questions": [
        { "q": "Ce metode de plată sunt acceptate?", "a": "Plățile se efectuează prin Stripe. Sunt acceptate cardul de credit (Visa, Mastercard, Amex) și debitul direct SEPA." },
        { "q": "Unde îmi găsesc facturile?", "a": "În Portalul vânzătorului la „Facturi\" sau în Portalul admin de grup. Toate facturile pot fi descărcate în PDF." },
        { "q": "Ce este un mandat SEPA?", "a": "Mandatul SEPA autorizează Surplify să încaseze plăți din contul tău bancar. Îl poți genera în setări după ce ți-ai salvat IBAN-ul/BIC-ul." },
        { "q": "Cum este validat codul meu TVA?", "a": "Surplify îți validează automat codul TVA prin sistemul VIES al UE. Introdu-l în setările portalului tău." },
        { "q": "Se aplică taxarea inversă?", "a": "Da, automat pentru un cod TVA UE valid din afara Germaniei. Factura include atunci mențiunea corespunzătoare." }
      ]
    },
    {
      "category": "Grupuri și locații",
      "questions": [
        { "q": "Ce este un grup de companii?", "a": "Un grup de companii reunește mai multe locații ale aceleiași organizații. Grupul este gestionat și facturat centralizat." },
        { "q": "Cum invit locații în grupul meu?", "a": "În Portalul admin de grup la „Invitații\". Poți invita prin e-mail sau prin căutarea locațiilor. Invitațiile expiră după un timp." },
        { "q": "Poate o locație să aparțină mai multor grupuri?", "a": "Nu, o locație poate aparține unui singur grup la un moment dat." }
      ]
    },
    {
      "category": "Portal extern",
      "questions": [
        { "q": "Pentru cine este portalul extern?", "a": "Pentru parteneri externi ocazionali care doresc să răspundă la solicitări sau să trimită oferte fără un cont complet." },
        { "q": "Cum mă înregistrez ca furnizor extern?", "a": "Printr-un link de invitație de la un manager sau prin pagina de înregistrare publică pentru furnizorii externi." },
        { "q": "Pot trece de la un cont extern la unul complet?", "a": "Da, upgrade-ul este posibil: contactează asistența sau managerul care te-a invitat." }
      ]
    },
    {
      "category": "Administrare",
      "questions": [
        { "q": "Ce poate face un admin de platformă?", "a": "Gestionarea utilizatorilor, gestionarea abonamentelor, comisioane, mandate SEPA, facturi, export DATEV, scenarii, jurnal de audit și toate panourile analitice." },
        { "q": "Ce este exportul DATEV?", "a": "Exportul DATEV generează date contabile în format DATEV pentru contabil. Este disponibil în panoul de administrare (fila Exporturi)." },
        { "q": "Cum gestionez comisioanele?", "a": "În panoul de comisioane administratorii pot configura modele de comisioane, pot examina tranzacțiile și pot crea decontări." },
        { "q": "Ce este jurnalul de audit?", "a": "Un jurnal imutabil al fiecărei acțiuni relevante pentru securitate (autentificări, schimbări de rol, ajustări de facturi), disponibil în panoul de administrare." }
      ]
    },
    {
      "category": "Dezvoltator și API",
      "questions": [
        { "q": "Cum obțin o cheie API?", "a": "În portalul pentru dezvoltatori la „Chei API\" poți crea o cheie nouă. Păstreaz-o în siguranță: este afișată o singură dată." },
        { "q": "Există limite de rată?", "a": "Da, pe cheie API. Limitele exacte sunt vizibile în portalul pentru dezvoltatori și depind de nivelul tău. La depășire primești un HTTP 429." },
        { "q": "Cum validez semnăturile webhook?", "a": "Fiecare payload este semnat cu HMAC-SHA256 (antet X-Surplify-Signature). Folosește secretul webhook-ului pentru a valida; exemple de cod se găsesc în documentația API." },
        { "q": "Ce este create-surplify-app?", "a": "Un instrument oficial de scaffolding (npx create-surplify-app my-integration) care creează un proiect de integrare preconfigurat cu listener de webhook, client API și teste în câteva secunde." },
        { "q": "Ce SDK-uri sunt disponibile?", "a": "Python, Node.js și PHP. Toate sunt generate dinamic și includ gestionarea autentificării, reîncercări și modele tipizate." }
      ]
    },
    {
      "category": "Asistență și limbi",
      "questions": [
        { "q": "Cum raportez o eroare?", "a": "Apasă pe pictograma insectă din antet sau deschide „Rapoartele mele de eroare\". Descrie problema și atașează opțional o captură de ecran." },
        { "q": "Cum schimb limba?", "a": "În colțul din dreapta sus al antetului găsești globul: apasă pe el și selectează limba ta. Alegerea ta este salvată." },
        { "q": "Cum contactez asistența?", "a": "Pentru chestiuni urgente folosește datele de contact din informațiile legale sau scrie direct din portal („Ajutor > Contactează asistența\")." }
      ]
    }
  ]
};
